// src/scenes/PhilosophyFlowScene.js
// v5 - FINAL COMPLETE FILE: Emoji Grid, Color Wheel, AI Reasoning.

import Phaser from 'phaser';
import BaseMenuScene from "./BaseMenuScene.js";
import MenuUIUtils from "../ui/MenuUIUtils.js";
import { auth, db, firebase } from "../firebaseConfig.js";
import claudeAPIService from "../services/claudeAPIService.js";
import sceneManager from "../utils/SceneManager.js";
import { hasSpecialAccess } from '../services/specialAccessService.js';
// REMOVED: MobileUIComponents - now handled by UnifiedMobileSystem

// --- Constants ---
const STEP_EMOJI = 1; // Now uses Grid
const STEP_COLOR = 2; // Uses Color Wheel
const STEP_SLEEP = 3; // Uses Buttons
const STEP_GENERATING = 4;
const STEP_DISPLAY_QUOTE = 5; // Includes Reasoning
const STEP_RATE_LIMITED = 6;

// UNIFIED: Use centralized mobile detection for constants
import { MobileDetectionService } from '../services/MobileDetectionService.js';
const isMobile = MobileDetectionService.isMobile();
const FADE_DURATION = isMobile ? 400 : 250;  // Slower fades on mobile
const STAGGER_DELAY = isMobile ? 120 : 80;   // More deliberate timing on mobile
const ANIMATION_WAIT_TIME = isMobile ? 6000 : 4000;  // Longer wait for mobile processing
const SCENE_INSTANCE_ID = Math.random().toString(36).substring(7); // For logging

const WHEEL_SIZE = 280; // Display size for the color wheel PNG
const SELECTOR_SIZE = 18; // Size of the indicator on the wheel
const COLOR_DISPLAY_SIZE = 50; // Size of the square showing selected color

// --- Mobile-Optimized Data Mappings ---
// Two-level emotion hierarchy from mobile implementation guide
const EMOTION_CATEGORIES = {
    'positive-high': [
        { emoji: '🥳', label: 'Excited', description: 'Feeling energized and enthusiastic' },
        { emoji: '🤩', label: 'Inspired', description: 'Feeling motivated and creative' },
        { emoji: '😎', label: 'Confident', description: 'Feeling assured and capable' },
        { emoji: '🔥', label: 'Energized', description: 'Feeling powerful and driven' }
    ],
    'positive-calm': [
        { emoji: '😌', label: 'Peaceful', description: 'Feeling calm and centered' },
        { emoji: '😊', label: 'Content', description: 'Feeling satisfied and happy' },
        { emoji: '🤔', label: 'Thoughtful', description: 'Feeling reflective and contemplative' },
        { emoji: '😇', label: 'Grateful', description: 'Feeling appreciative and blessed' }
    ],
    'challenging': [
        { emoji: '😟', label: 'Worried', description: 'Feeling concerned about outcomes' },
        { emoji: '😢', label: 'Sad', description: 'Feeling down or melancholic' },
        { emoji: '😨', label: 'Anxious', description: 'Feeling nervous or uncertain' },
        { emoji: '😤', label: 'Frustrated', description: 'Feeling blocked or hindered' }
    ],
    'intense': [
        { emoji: '😠', label: 'Angry', description: 'Feeling upset or irritated' },
        { emoji: '😡', label: 'Furious', description: 'Feeling intense anger' },
        { emoji: '🤯', label: 'Overwhelmed', description: 'Feeling too much at once' },
        { emoji: '😱', label: 'Shocked', description: 'Feeling surprised or stunned' }
    ]
};

// Executive color palette from mobile implementation guide
const EXECUTIVE_COLOR_PALETTE = [
    { hex: '#FF6B6B', name: 'Executive Red', mood: 'Power' },
    { hex: '#FFA500', name: 'Strategic Orange', mood: 'Innovation' },
    { hex: '#FFD700', name: 'Leadership Gold', mood: 'Excellence' },
    { hex: '#98D8C8', name: 'Clarity Mint', mood: 'Focus' },
    { hex: '#00D4FF', name: 'DeckOps Blue', mood: 'Intelligence' },
    { hex: '#8B5CF6', name: 'Visionary Purple', mood: 'Insight' },
    { hex: '#10B981', name: 'Growth Green', mood: 'Progress' },
    { hex: '#EC4899', name: 'Creative Pink', mood: 'Innovation' },
    { hex: '#F59E0B', name: 'Ambition Amber', mood: 'Drive' },
    { hex: '#6366F1', name: 'Trust Indigo', mood: 'Stability' },
    { hex: '#14B8A6', name: 'Balance Teal', mood: 'Harmony' },
    { hex: '#F43F5E', name: 'Passion Rose', mood: 'Energy' }
];

// Legacy mappings for compatibility
const EMOJI_FEELINGS_MAP = [
    { symbol: '😠', feeling: 'Angry' }, { symbol: '😟', feeling: 'Worried' },
    { symbol: '😢', feeling: 'Sad' }, { symbol: '😨', feeling: 'Fearful' },
    { symbol: '🥱', feeling: 'Tired' }, { symbol: '😐', feeling: 'Neutral' },
    { symbol: '🤔', feeling: 'Thinking' }, { symbol: '😊', feeling: 'Happy' },
    { symbol: '🥳', feeling: 'Excited' }, { symbol: '🤩', feeling: 'Amazed' },
    { symbol: '😎', feeling: 'Confident' }, { symbol: '🔥', feeling: 'Passionate' }
];

const COLOR_MOODS_MAP = [ // Legacy mapping
    { mood: 'Passion', color: '#FF3333' }, { mood: 'Joy', color: '#FFAA00' },
    { mood: 'Optimism', color: '#FFDD55' }, { mood: 'Energy', color: '#AAFF55' },
    { mood: 'Calm', color: '#55FF88' }, { mood: 'Clarity', color: '#55FFFF' },
    { mood: 'Sadness', color: '#5588FF' }, { mood: 'Creativity', color: '#AA55FF' },
    { mood: 'Compassion', color: '#FF88CC'}, { mood: 'Peace', color: '#FFFFFF' },
    { mood: 'Neutral', color: '#808080' }, { mood: 'Serious', color: '#1A1A1A' }
];
// --- End Constants & Mappings ---


class PhilosophyFlowScene extends BaseMenuScene {
    constructor() {
        super({ key: "PhilosophyFlowScene" });
        
        // ENTERPRISE UPGRADE: Load template CSS for glass morphism
        this.loadTemplateCSS();
        // Initialize state variables
        this.currentStep = STEP_EMOJI;
        this.selectedFeeling = null;
        this.selectedEmojiSymbol = null;
        this.selectedMood = null;
        this.selectedHexColor = '#FFFFFF'; // Default color
        this.selectedColorHue = 0; // Default hue
        this.selectedSleep = null;
        this.quoteData = null; // Will store { quote, figure, reasoning }
        this.lastQuoteTimestamp = null;
        this.isRateLimited = false;
        this.isAdmin = false;
        this.claudeService = new claudeAPIService();
        this.stepContainer = null; // Holds UI elements for the current step
        this.feedbackTween = null; // For button press feedback
        this.uiElements = {}; // Stores references to UI elements within a step
        this.isTransitioningSteps = false; // Prevents overlapping transitions

        // Wheel/Grid specific references
        this.activeWheelSprite = null; // For the color wheel image
        this.wheelSelectorSprite = null; // Indicator for color wheel
        this.wheelColorDisplayRect = null; // Box showing selected color
        // No wheel needed for emoji step now
        this.selectedEmojiButton = null;
        
        // Mobile-specific properties
        this.mobileBottomSheet = null;
        this.mobileProgressPanel = null;
        this.hapticFeedbackEnabled = 'vibrate' in navigator;
    }

    // --- Lifecycle Methods ---
       // --- REPLACE THIS METHOD ---
        // --- REPLACE init METHOD ---
    async init(data) {
        super.init(data);
        const sceneInstanceId = SCENE_INSTANCE_ID;
        console.log(`[${this.scene.key}-${sceneInstanceId}] === INIT START ===`);
        console.log(`[${this.scene.key}-${sceneInstanceId}] Received data:`, data);

        // Reset instance variables
        this.currentStep = STEP_EMOJI; // Default assumption
        this.selectedFeeling = null; this.selectedEmojiSymbol = null; this.selectedEmojiButton = null;
        this.selectedMood = null; this.selectedHexColor = '#FFFFFF'; this.selectedColorHue = 0;
        this.selectedSleep = null; this.quoteData = null; this.lastQuoteTimestamp = null;
        this.isRateLimited = false; // *** Default to NOT rate limited ***
        this.isAdmin = false; this.stepContainer = null; this.uiElements = {};
        this.isTransitioningSteps = false; this.activeWheelSprite = null;
        this.wheelSelectorSprite = null; this.wheelColorDisplayRect = null;
        console.log(`[${this.scene.key}-${sceneInstanceId}] Initial state variables reset.`);

        // Determine user status
        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();

        console.log(`[${this.scene.key}-${sceneInstanceId}] Auth User Check: ${user ? user.uid : 'null'}, Special Access: ${specialAccess}`);

        if (user) {
            // --- Logic for AUTHENTICATED users ---
            console.log(`[${this.scene.key}-${sceneInstanceId}] Path: Authenticated User`);
            try {
                const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
                if (profileDoc.exists) {
                    this.lastQuoteTimestamp = profileDoc.data().lastPhilosophyQuoteTimestamp || null;
                    this.isAdmin = profileDoc.data().role === 'admin';
                    console.log(`[${this.scene.key}-${sceneInstanceId}] Profile loaded. Admin: ${this.isAdmin}. Last Timestamp:`, this.lastQuoteTimestamp ? this.lastQuoteTimestamp.toDate() : 'None');

                    if (!this.isAdmin && this.lastQuoteTimestamp) { // Check timestamp only if exists & not admin
                        console.log(`[${this.scene.key}-${sceneInstanceId}] Non-admin user, checking rate limit...`);
                        this.performNormalRateLimitCheck(); // Modifies this.isRateLimited
                        console.log(`[${this.scene.key}-${sceneInstanceId}] Rate limit check result: isRateLimited = ${this.isRateLimited}`);
                    } else if (this.isAdmin) {
                        this.isRateLimited = false; // Admins always bypass
                        console.log(`[${this.scene.key}-${sceneInstanceId}] Admin bypass for rate limit. isRateLimited = ${this.isRateLimited}`);
                    } else {
                        this.isRateLimited = false; // No timestamp means no limit
                         console.log(`[${this.scene.key}-${sceneInstanceId}] No previous timestamp found, not rate limited. isRateLimited = ${this.isRateLimited}`);
                    }
                } else {
                    console.log(`[${this.scene.key}-${sceneInstanceId}] No user profile found, not rate limited.`);
                    this.isRateLimited = false; // No profile = no limit
                }
            } catch (error) {
                console.error("Error fetching user profile:", error);
                this.isRateLimited = false; // Default to no rate limit on DB error
            }
        } else {
            // --- Logic for GUESTS (Regular and Guest+) ---
            this.isRateLimited = false; // <<<--- *** Explicitly FALSE for ALL guests ***
            console.log(`[${this.scene.key}-${sceneInstanceId}] Path: Guest or Guest+ User. Set isRateLimited = ${this.isRateLimited}`);
        }

        // *** CRITICAL: Set the starting step based ONLY on the final isRateLimited value ***
        this.currentStep = this.isRateLimited ? STEP_RATE_LIMITED : STEP_EMOJI;
        // *** END CRITICAL ***

        console.log(`[${this.scene.key}-${sceneInstanceId}] === INIT END === Final State: Step=${this.currentStep}, RateLimited=${this.isRateLimited}`);
    }
    // --- END REPLACE init METHOD ---

        // --- REPLACE create METHOD ---
        // --- REPLACE create METHOD ---
    create() {
        try {
            super.create();
            console.log('🎭 Philosophy Flow: Creating DOM interface...');
            
            // ENTERPRISE UPGRADE: Use template system instead of old Phaser UI
            this.isMobile = MobileDetectionService.isMobile();
            console.log('🎭 Philosophy Flow: Mobile device detected:', this.isMobile);
            console.log('🎨 Philosophy Flow: Using template CSS system for enterprise quality');
            
            // Load template CSS for glass morphism
            this.loadTemplateCSS();
            
            this.createPhilosophyFlowDOM();
            
            // Initialize mobile enhancements if on mobile
            if (this.isMobile) {
                this.initializeMobileEnhancements();
            }
        } catch (error) {
            alert('Philosophy Flow error: ' + error.message);
        }
    }
    
    initializeMobileEnhancements() {
        console.log('📱 Philosophy Flow: Initializing mobile enhancements...');
        
        // Apply mobile-responsive CSS to existing DOM
        this.addMobileResponsiveCSS();
        
        // Enhance existing UI with mobile touch optimization
        this.optimizeForMobileTouch();
        
        // Create mobile progress panel using existing patterns
        this.createMobileProgressPanel();
        
        // Add haptic feedback to interactions
        this.setupHapticFeedback();
        
        // Create mobile-specific bottom sheet for quotes using existing BottomSheet
        this.createMobileQuoteBottomSheet();
        
        // Add mobile gesture shortcuts
        this.setupMobileGestureShortcuts();
        
        console.log('✅ Philosophy Flow: Mobile enhancements initialized');
    }
    
    addMobileResponsiveCSS() {
        if (document.getElementById('philosophy-mobile-responsive')) return;
        
        const style = document.createElement('style');
        style.id = 'philosophy-mobile-responsive';
        style.textContent = `
            /* Mobile-responsive enhancements for Philosophy Flow */
            @media (max-width: 768px) {
                .philosophy-executive-app {
                    padding: 0 !important;
                }
                
                .philosophy-flow-content {
                    padding: 20px 16px !important;
                    margin: 0 !important;
                }
                
                .step-title {
                    font-size: 24px !important;
                }
                
                .step-subtitle {
                    font-size: 16px !important;
                    margin-bottom: 30px !important;
                }
                
                .emoji-option {
                    width: 90px !important;
                    height: 90px !important;
                    border-radius: 20px !important;
                    min-height: 44px !important;
                    touch-action: manipulation !important;
                    -webkit-tap-highlight-color: transparent !important;
                }
                
                .color-option {
                    min-height: 60px !important;
                    padding: 16px !important;
                    touch-action: manipulation !important;
                }
                
                .sleep-option {
                    min-height: 60px !important;
                    padding: 16px !important;
                    touch-action: manipulation !important;
                }
                
                .primary-button {
                    min-height: 56px !important;
                    padding: 18px 24px !important;
                    font-size: 18px !important;
                    border-radius: 16px !important;
                    touch-action: manipulation !important;
                    -webkit-tap-highlight-color: transparent !important;
                }
                
                .journal-input {
                    font-size: 16px !important; /* Prevent iOS zoom */
                    padding: 16px !important;
                    border-radius: 12px !important;
                    min-height: 120px !important;
                }
            }
        `;
        
        document.head.appendChild(style);
    }
    
    optimizeForMobileTouch() {
        // Add touch feedback to existing elements
        const overlay = document.getElementById('philosophy-flow-overlay');
        if (!overlay) return;
        
        // Add touch feedback to buttons
        overlay.addEventListener('touchstart', (e) => {
            const button = e.target.closest('.primary-button, .emoji-option, .color-option, .sleep-option');
            if (button) {
                button.style.transform = 'scale(0.95)';
                button.style.opacity = '0.8';
                
                // Haptic feedback
                if ('vibrate' in navigator) {
                    navigator.vibrate([10]);
                }
            }
        }, { passive: true });
        
        overlay.addEventListener('touchend', (e) => {
            const button = e.target.closest('.primary-button, .emoji-option, .color-option, .sleep-option');
            if (button) {
                setTimeout(() => {
                    button.style.transform = '';
                    button.style.opacity = '';
                }, 100);
            }
        }, { passive: true });
    }
    
    createMobileProgressPanel() {
        this.mobileProgressPanel = new SlideUpPanel({
            position: 'top',
            size: '80px',
            overlay: false,
            swipeToClose: false
        });
        
        const progressContent = document.createElement('div');
        progressContent.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 600;
        `;
        
        progressContent.innerHTML = `
            <div style="display: flex; align-items: center; gap: 15px;">
                <div class="progress-steps">
                    <span class="step active">🎭</span>
                    <span class="step">🎨</span>
                    <span class="step">😴</span>
                    <span class="step">💭</span>
                </div>
                <div class="progress-text">Select Your Feeling</div>
            </div>
        `;
        
        this.mobileProgressPanel.setContent(progressContent);
        this.mobileProgressPanel.open();
    }
    
    updateMobileProgress(step, stepText) {
        if (!this.mobileProgressPanel || !this.isMobile) return;
        
        const steps = this.mobileProgressPanel.content.querySelectorAll('.step');
        const progressText = this.mobileProgressPanel.content.querySelector('.progress-text');
        
        // Update step indicators
        steps.forEach((stepEl, index) => {
            stepEl.className = index < step ? 'step completed' : 
                              index === step - 1 ? 'step active' : 'step';
        });
        
        // Update progress text
        if (progressText) {
            progressText.textContent = stepText;
        }
        
        // Add haptic feedback
        this.triggerHapticFeedback('light');
    }
    
    createMobileQuoteBottomSheet() {
        this.mobileBottomSheet = new BottomSheet({
            height: '70vh',
            snapPoints: ['40%', '70%', '90%'],
            backdrop: true,
            dismissible: true
        });
        
        // Listen for bottom sheet events
        this.mobileBottomSheet.element.addEventListener('bottomsheet:close', () => {
            console.log('📱 Philosophy Flow: Quote bottom sheet closed');
        });
    }
    
    showMobileQuoteDisplay(quoteData) {
        if (!this.mobileBottomSheet || !this.isMobile) return;
        
        const quoteContent = document.createElement('div');
        quoteContent.style.cssText = `
            padding: 20px 0;
        `;
        
        quoteContent.innerHTML = `
            <div class="mobile-quote-header">
                <h2 style="margin: 0 0 10px 0; font-size: 20px; font-weight: 700; color: #1f2937;">
                    💭 Your Philosophical Insight
                </h2>
                <div style="height: 3px; background: linear-gradient(90deg, #667eea, #764ba2); border-radius: 2px; margin-bottom: 20px;"></div>
            </div>
            
            <div class="mobile-quote-content">
                <blockquote style="
                    font-size: 18px;
                    line-height: 1.6;
                    font-style: italic;
                    margin: 0 0 20px 0;
                    padding: 20px;
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    border-radius: 12px;
                    border-left: 4px solid #667eea;
                    color: #374151;
                ">
                    "${quoteData.quote}"
                </blockquote>
                
                <div style="text-align: right; margin-bottom: 20px;">
                    <cite style="font-size: 16px; font-weight: 600; color: #6b7280;">
                        — ${quoteData.figure}
                    </cite>
                </div>
                
                ${quoteData.reasoning ? `
                <div class="reasoning-section">
                    <h3 style="font-size: 16px; font-weight: 600; color: #374151; margin: 0 0 12px 0;">
                        🧠 Why This Resonates
                    </h3>
                    <p style="font-size: 14px; line-height: 1.5; color: #6b7280; margin: 0;">
                        ${quoteData.reasoning}
                    </p>
                </div>
                ` : ''}
            </div>
            
            <div class="mobile-quote-actions" style="
                display: flex;
                gap: 12px;
                margin-top: 30px;
                flex-wrap: wrap;
            ">
                <button class="quote-action-btn favorite-btn" style="
                    flex: 1;
                    min-width: 120px;
                    padding: 14px 20px;
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                ">
                    ⭐ Save Favorite
                </button>
                
                <button class="quote-action-btn share-btn" style="
                    flex: 1;
                    min-width: 120px;
                    padding: 14px 20px;
                    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                ">
                    📱 Share
                </button>
                
                <button class="quote-action-btn new-session-btn" style="
                    flex: 1;
                    min-width: 120px;
                    padding: 14px 20px;
                    background: linear-gradient(135deg, #10b981, #059669);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                ">
                    🔄 New Session
                </button>
            </div>
        `;
        
        // Add event listeners
        const favoriteBtn = quoteContent.querySelector('.favorite-btn');
        const shareBtn = quoteContent.querySelector('.share-btn');
        const newSessionBtn = quoteContent.querySelector('.new-session-btn');
        
        favoriteBtn.addEventListener('click', () => {
            this.handleMobileFavorite(quoteData);
        });
        
        shareBtn.addEventListener('click', () => {
            this.handleMobileShare(quoteData);
        });
        
        newSessionBtn.addEventListener('click', () => {
            this.handleMobileNewSession();
        });
        
        this.mobileBottomSheet.setContent(quoteContent);
        this.mobileBottomSheet.open();
        
        // Trigger haptic feedback for quote reveal
        this.triggerHapticFeedback('medium');
    }
    
    handleMobileFavorite(quoteData) {
        // Save to offline storage
        if (window.mobileOfflineManager) {
            window.mobileOfflineManager.savePhilosophyFlowSession({
                type: 'favorite_quote',
                quote: quoteData,
                timestamp: Date.now()
            });
        }
        
        MobileToast.show('Quote saved to favorites! ⭐', {
            type: 'success',
            duration: 2000
        });
        
        this.triggerHapticFeedback('light');
    }
    
    handleMobileShare(quoteData) {
        if (navigator.share) {
            navigator.share({
                title: 'Philosophy Flow Insight',
                text: `"${quoteData.quote}" — ${quoteData.figure}`,
                url: window.location.href
            }).then(() => {
                MobileToast.show('Quote shared successfully! 📱', {
                    type: 'success'
                });
            }).catch(console.error);
        } else {
            // Fallback: copy to clipboard
            const shareText = `"${quoteData.quote}" — ${quoteData.figure}\n\nGenerated by Philosophy Flow at DeckOps.ai`;
            navigator.clipboard.writeText(shareText).then(() => {
                MobileToast.show('Quote copied to clipboard! 📋', {
                    type: 'info'
                });
            }).catch(() => {
                MobileToast.show('Unable to share quote', {
                    type: 'error'
                });
            });
        }
        
        this.triggerHapticFeedback('light');
    }
    
    handleMobileNewSession() {
        this.mobileBottomSheet.close();
        
        // Reset session state
        this.currentStep = STEP_EMOJI;
        this.selectedFeeling = null;
        this.selectedEmojiSymbol = null;
        this.selectedMood = null;
        this.selectedSleep = null;
        this.quoteData = null;
        
        // Show new session starting
        MobileToast.show('Starting new Philosophy Flow session... 🎭', {
            type: 'info'
        });
        
        // Update progress
        this.updateMobileProgress(1, 'Select Your Feeling');
        
        // Restart the flow
        setTimeout(() => {
            this.showDOMStep(STEP_EMOJI);
        }, 500);
        
        this.triggerHapticFeedback('medium');
    }
    
    setupHapticFeedback() {
        if (!this.hapticFeedbackEnabled) {
            console.log('📱 Haptic feedback not supported on this device');
            return;
        }
        
        console.log('📳 Haptic feedback enabled for Philosophy Flow');
    }
    
    triggerHapticFeedback(intensity = 'light') {
        if (!this.hapticFeedbackEnabled || !this.isMobile) return;
        
        try {
            const patterns = {
                light: [10],
                medium: [20],
                strong: [50],
                success: [10, 50, 10],
                error: [100, 50, 100]
            };
            
            navigator.vibrate(patterns[intensity] || patterns.light);
        } catch (error) {
            console.warn('Haptic feedback failed:', error);
        }
    }
    
    setupMobileGestureShortcuts() {
        // UNIFIED: Mobile gestures handled by UnifiedMobileSystem
        console.log('🎭 Philosophy Flow mobile gestures delegated to UnifiedMobileSystem');
    }
        // --- END REPLACE create METHOD ---

    createPhilosophyFlowDOM() {
        console.log('🎭 Creating Philosophy Flow DOM overlay (like PPT Hero analytics)...');
        
        // Remove any existing Philosophy Flow overlay
        const existingOverlay = document.getElementById('philosophy-flow-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }
        
        // ENTERPRISE UPGRADE: Create overlay with GLASS MORPHISM template styling
        const overlay = document.createElement('div');
        overlay.id = 'philosophy-flow-overlay';
        overlay.className = 'glass-morphism-overlay template-overlay';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            color: rgba(255, 255, 255, 0.9);
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 16px;
            z-index: 10000;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            display: flex;
            align-items: center;
            justify-content: center;
        `;
        
        overlay.innerHTML = `
            <div class="philosophy-executive-app">
                <!-- Executive Header -->
                <header class="executive-header">
                    <div class="header-content">
                        <div class="brand-section">
                            <div class="executive-logo">DeckOps.ai</div>
                            <div class="divider"></div>
                            <div class="feature-title">Philosophy Flow</div>
                        </div>
                        <button class="back-control" onclick="sceneManager.switchToScene('AuthenticatedMenuSceneDOM')">← Executive Dashboard</button>
                    </div>
                </header>
                
                <!-- Main Content Area -->
                <main class="content-area">
                    <div id="philosophy-flow-content"></div>
                </main>
            </div>
        `;
        
        // Add to DOM
        document.body.appendChild(overlay);
        
        // CRITICAL: Add styles BEFORE showing content
        this.addPhilosophyFlowStyles();
        
        // Initialize state variables
        this.selectedCategory = null;
        this.selectedEmotion = null;
        this.selectedColor = null;
        this.selectedSleep = null;
        this.quoteData = null;
        this.isFavorited = false;
        this.isSaved = false;
        this.executiveNote = '';
        
        // Start with step 1 (category selection)
        this.currentStep = this.isRateLimited ? 6 : 1;
        this.showDOMStep(this.currentStep);
        
        console.log('🎭 Philosophy Flow DOM overlay created and shown');
    }
    
    populatePhilosophyFlowContent() {
        const contentDiv = document.querySelector("#philosophy-flow-overlay #philosophy-flow-content");
        if (!contentDiv) {
            console.error("Philosophy Flow content container not found!");
            return;
        }
        
        // Add Philosophy Flow specific styling
        this.addPhilosophyFlowStyles();
        
        // Start with step 1 (feeling selection)
        const startStep = this.isRateLimited ? 6 : 1;
        this.currentStep = startStep;
        this.showDOMStep(startStep);
    }
    
    addPhilosophyFlowStyles() {
        // Add styles if not already present
        if (document.getElementById('philosophy-flow-styles')) return;
        
        const style = document.createElement('style');
        style.id = 'philosophy-flow-styles';
        style.textContent = `
            /* Executive Philosophy Flow Styles - Full Screen Layout */
            .philosophy-executive-app {
                min-height: 100vh;
                width: 100%;
                display: flex;
                flex-direction: column;
                position: relative;
                background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            }
            
            .executive-header {
                padding: 32px 48px 24px;
                background: linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.6) 100%);
                backdrop-filter: blur(20px);
                border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                position: relative;
                z-index: 10;
            }
            
            .header-content {
                max-width: 1400px;
                margin: 0 auto;
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
            
            .brand-section {
                display: flex;
                align-items: center;
                gap: 16px;
            }
            
            .executive-logo {
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%);
                background-clip: text;
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                font-size: 28px;
                font-weight: 800;
                letter-spacing: -1px;
            }
            
            .divider {
                width: 1px;
                height: 40px;
                background: linear-gradient(to bottom, transparent, #00d4ff, transparent);
            }
            
            .feature-title {
                font-size: 20px;
                font-weight: 600;
                color: rgba(255, 255, 255, 0.9);
            }
            
            .back-control {
                background: rgba(255, 255, 255, 0.05);
                backdrop-filter: blur(15px);
                border: 1px solid rgba(255, 255, 255, 0.1);
                color: white;
                padding: 12px 20px;
                border-radius: 10px;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.3s ease;
            }
            
            .back-control:hover {
                background: rgba(255, 255, 255, 0.08);
                transform: translateY(-2px);
                border-color: #00d4ff;
            }
            
            .content-area {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 48px 32px;
                position: relative;
                min-height: calc(100vh - 140px);
            }
            
            #philosophy-flow-content {
                width: 100%;
                max-width: 1200px;
                margin: 0 auto;
            }
            
            .step-title {
                text-align: center;
                margin-bottom: 64px;
            }
            
            .step-number {
                font-size: 11px;
                color: rgba(255, 255, 255, 0.7);
                text-transform: uppercase;
                letter-spacing: 2px;
                margin-bottom: 12px;
            }
            
            .step-heading {
                font-size: 36px;
                font-weight: 700;
                background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%);
                background-clip: text;
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                line-height: 1.2;
                margin-bottom: 16px;
            }
            
            .step-subtitle {
                font-size: 16px;
                color: rgba(255, 255, 255, 0.8);
                line-height: 1.5;
            }
            
            .category-grid {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                grid-template-rows: repeat(2, 1fr);
                gap: 32px;
                max-width: 1200px;
                margin: 0 auto;
                padding: 0 20px;
            }
            
            .category-card {
                background: rgba(255, 255, 255, 0.05);
                backdrop-filter: blur(15px);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 20px;
                padding: 40px 32px;
                text-align: center;
                cursor: pointer;
                transition: all 0.3s ease;
                position: relative;
                overflow: hidden;
                min-height: 280px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
            }
            
            .category-card:hover {
                background: rgba(255, 255, 255, 0.08);
                transform: translateY(-6px);
                border-color: #00d4ff;
                box-shadow: 0 25px 50px rgba(0, 212, 255, 0.15);
            }
            
            .category-card.selected {
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                border-color: #00d4ff;
                color: white;
                box-shadow: 0 25px 50px rgba(0, 212, 255, 0.3);
            }
            
            .category-icon {
                font-size: 56px;
                margin-bottom: 24px;
                display: block;
                opacity: 0.9;
            }
            
            .category-label {
                font-size: 22px;
                font-weight: 700;
                margin-bottom: 16px;
                line-height: 1.3;
            }
            
            .category-description {
                font-size: 16px;
                line-height: 1.5;
                margin-bottom: 20px;
                opacity: 0.9;
            }
            
            .category-examples {
                font-size: 12px;
                opacity: 0.7;
                font-style: italic;
                line-height: 1.4;
            }
            
            .category-emotions-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 24px;
                max-width: 900px;
                margin: 0 auto 40px;
                padding: 0 20px;
            }
            
            .emotion-card {
                background: rgba(15, 23, 42, 0.8);
                backdrop-filter: blur(15px);
                border: 2px solid rgba(255, 255, 255, 0.15);
                border-radius: 16px;
                padding: 24px 16px;
                text-align: center;
                cursor: pointer;
                transition: all 0.3s ease;
                position: relative;
                overflow: hidden;
                min-height: 140px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            }
            
            .emotion-card:hover {
                background: rgba(255, 255, 255, 0.08);
                transform: translateY(-4px);
                border-color: #00d4ff;
                box-shadow: 0 20px 40px rgba(0, 212, 255, 0.15);
            }
            
            .emotion-card.selected {
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                border-color: #00d4ff;
                color: white;
                box-shadow: 0 20px 40px rgba(0, 212, 255, 0.3);
            }
            
            .emotion-icon {
                font-size: 32px;
                margin-bottom: 12px;
                display: block;
            }
            
            .emotion-label {
                font-size: 15px;
                font-weight: 600;
                margin-bottom: 6px;
                line-height: 1.2;
                color: rgba(255, 255, 255, 0.95);
                text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
            }
            
            .emotion-description {
                font-size: 11px;
                opacity: 0.9;
                line-height: 1.3;
                text-align: center;
                color: rgba(255, 255, 255, 0.8);
                text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
            }
            
            .executive-button {
                background: rgba(255, 255, 255, 0.05);
                backdrop-filter: blur(15px);
                border: 1px solid rgba(255, 255, 255, 0.1);
                color: white;
                padding: 16px 32px;
                border-radius: 12px;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.3s ease;
            }
            
            .executive-button:hover {
                background: rgba(255, 255, 255, 0.08);
                transform: translateY(-2px);
                border-color: #00d4ff;
            }
            
            .executive-button.primary {
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                border-color: #00d4ff;
                color: white;
                box-shadow: 0 8px 25px rgba(0, 212, 255, 0.3);
            }
            
            .executive-button.primary:hover:not(:disabled) {
                transform: translateY(-3px);
                box-shadow: 0 12px 35px rgba(0, 212, 255, 0.4);
            }
            
            .executive-button:disabled {
                opacity: 0.5;
                cursor: not-allowed;
                transform: none;
            }
            
            /* Color Analysis Section */
            .color-analysis-section {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 48px;
            }
            
            .color-wheel-container {
                position: relative;
            }
            
            .executive-color-grid {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 16px;
                max-width: 600px;
                margin: 0 auto 40px;
                padding: 0 20px;
            }
            
            .color-option {
                background: rgba(15, 23, 42, 0.8);
                backdrop-filter: blur(15px);
                border: 2px solid rgba(255, 255, 255, 0.2);
                border-radius: 12px;
                padding: 20px 16px;
                text-align: center;
                cursor: pointer;
                transition: all 0.3s ease;
                position: relative;
                overflow: hidden;
                box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            }
            
            .color-option:hover {
                background: rgba(255, 255, 255, 0.08);
                transform: translateY(-4px);
                border-color: #00d4ff;
                box-shadow: 0 15px 30px rgba(0, 212, 255, 0.15);
            }
            
            .color-option.selected {
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                border-color: #00d4ff;
                box-shadow: 0 15px 30px rgba(0, 212, 255, 0.4);
                transform: translateY(-2px);
            }
            
            .color-option.selected .color-name {
                color: #ffffff;
                font-weight: 700;
            }
            
            .color-swatch {
                width: 50px;
                height: 50px;
                border-radius: 8px;
                margin: 0 auto 12px;
                border: 2px solid rgba(255, 255, 255, 0.2);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            }
            
            .color-name {
                font-size: 13px;
                font-weight: 600;
                color: rgba(255, 255, 255, 0.9);
                line-height: 1.2;
            }
            
            .color-feedback {
                text-align: center;
                background: rgba(255, 255, 255, 0.05);
                backdrop-filter: blur(15px);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 16px;
                padding: 32px;
                width: 400px;
                min-height: 180px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
            }
            
            .selected-color-display {
                width: 80px;
                height: 80px;
                border-radius: 12px;
                margin: 0 auto 16px;
                border: 2px solid rgba(255, 255, 255, 0.1);
                background: #FF3333;
                transition: all 0.3s ease;
            }
            
            .mood-name {
                font-size: 24px;
                font-weight: 600;
                margin-bottom: 8px;
                color: #ffffff;
            }
            
            .mood-insight {
                font-size: 14px;
                color: rgba(255, 255, 255, 0.8);
                line-height: 1.5;
            }
            
            /* Recovery Assessment Section */
            .recovery-section {
                max-width: 600px;
                margin: 0 auto;
            }
            
            .sleep-options {
                display: grid;
                grid-template-columns: 1fr;
                gap: 16px;
            }
            
            .sleep-option {
                background: rgba(15, 23, 42, 0.8);
                backdrop-filter: blur(15px);
                border: 2px solid rgba(255, 255, 255, 0.2);
                border-radius: 16px;
                padding: 24px 32px;
                cursor: pointer;
                transition: all 0.3s ease;
                display: flex;
                align-items: center;
                justify-content: space-between;
                position: relative;
                overflow: hidden;
                box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            }
            
            .sleep-option:hover {
                background: rgba(0, 212, 255, 0.1);
                transform: translateX(8px);
                border-color: #00d4ff;
                box-shadow: 0 8px 25px rgba(0, 212, 255, 0.3);
            }
            
            .sleep-option.selected {
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                border-color: #00d4ff;
                color: white;
                box-shadow: 0 8px 25px rgba(0, 212, 255, 0.4);
            }
            
            .sleep-content {
                display: flex;
                align-items: center;
                gap: 20px;
            }
            
            .sleep-duration {
                font-size: 24px;
                font-weight: 700;
                color: #00d4ff;
                min-width: 120px;
                text-align: left;
                text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
            }
            
            .sleep-description {
                font-size: 14px;
                color: rgba(255, 255, 255, 0.95);
                line-height: 1.4;
                text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
            }
            
            .sleep-option.selected .sleep-duration,
            .sleep-option.selected .sleep-description {
                color: #ffffff;
                text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
            }
            
            .sleep-indicator {
                font-size: 28px;
                color: rgba(255, 255, 255, 0.7);
            }
            
            /* Synthesis Container */
            .synthesis-container {
                text-align: center;
                max-width: 800px;
                margin: 0 auto;
            }
            
            .wisdom-engine {
                position: relative;
                width: 300px;
                height: 300px;
                margin: 0 auto 64px;
            }
            
            .central-processor {
                position: absolute;
                top: 50%;
                left: 50%;
                width: 80px;
                height: 80px;
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                border-radius: 50%;
                transform: translate(-50%, -50%);
                animation: coreProcessor 6s ease-in-out infinite;
                box-shadow: 0 0 40px rgba(0, 212, 255, 0.6), 0 0 80px rgba(0, 212, 255, 0.3);
            }
            
            .knowledge-ring {
                position: absolute;
                top: 50%;
                left: 50%;
                border-radius: 50%;
                transform: translate(-50%, -50%);
            }
            
            .ring-outer {
                width: 240px;
                height: 240px;
                background: linear-gradient(45deg, rgba(0, 212, 255, 0.2) 0%, transparent 50%, rgba(16, 185, 129, 0.2) 100%);
                border-radius: 50%;
                animation: ringRotate 60s linear infinite;
            }
            
            .ring-middle {
                width: 180px;
                height: 180px;
                background: linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, transparent 50%, rgba(245, 158, 11, 0.3) 100%);
                border-radius: 50%;
                animation: ringRotate 45s linear infinite reverse;
            }
            
            .ring-inner {
                width: 120px;
                height: 120px;
                background: linear-gradient(225deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%, rgba(0, 212, 255, 0.1) 100%);
                border-radius: 50%;
                animation: ringRotate 30s linear infinite;
            }
            
            .synthesis-status {
                margin-bottom: 32px;
            }
            
            .status-title {
                font-size: 24px;
                font-weight: 600;
                color: rgba(255, 255, 255, 0.9);
                margin-bottom: 16px;
            }
            
            .phase-text {
                font-size: 16px;
                color: rgba(255, 255, 255, 0.7);
                opacity: 0;
                animation: phaseSequence 16s ease-in-out infinite;
                font-style: italic;
            }
            
            .phase-text:nth-child(2) { animation-delay: 4s; }
            .phase-text:nth-child(3) { animation-delay: 8s; }
            .phase-text:nth-child(4) { animation-delay: 12s; }
            
            /* Quote Presentation */
            .quote-presentation {
                max-width: 1000px;
                margin: 0 auto;
            }
            
            .quote-content {
                text-align: center;
                margin-bottom: 64px;
                background: rgba(255, 255, 255, 0.02);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 16px;
                padding: 48px 32px;
                backdrop-filter: blur(15px);
                box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
            }
            
            .quote-text {
                font-size: 28px;
                font-style: italic;
                line-height: 1.6;
                margin-bottom: 32px;
                color: #ffffff;
                font-weight: 400;
                text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
                max-width: 800px;
                margin-left: auto;
                margin-right: auto;
            }
            
            .quote-attribution {
                font-size: 20px;
                font-weight: 600;
                color: #00d4ff;
                margin-bottom: 48px;
            }
            
            .insight-panel {
                background: rgba(255, 255, 255, 0.05);
                backdrop-filter: blur(15px);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 16px;
                padding: 48px;
                margin-bottom: 48px;
                text-align: left;
                box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
            }
            
            .insight-title {
                font-size: 14px;
                font-weight: 600;
                color: #00d4ff;
                margin-bottom: 16px;
                text-transform: uppercase;
                letter-spacing: 1px;
            }
            
            .insight-text {
                font-size: 16px;
                line-height: 1.6;
                color: rgba(255, 255, 255, 0.9);
                text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
            }
            
            /* Executive Actions */
            .executive-actions {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                gap: 20px;
                margin-bottom: 64px;
            }
            
            .action-card {
                background: rgba(255, 255, 255, 0.05);
                backdrop-filter: blur(15px);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 16px;
                padding: 32px;
                text-align: center;
                cursor: pointer;
                transition: all 0.3s ease;
                position: relative;
                overflow: hidden;
            }
            
            .action-card:hover {
                background: rgba(255, 255, 255, 0.08);
                transform: translateY(-4px);
                border-color: #00d4ff;
                box-shadow: 0 20px 40px rgba(0, 212, 255, 0.15);
            }
            
            .action-card.active {
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                border-color: #00d4ff;
                color: white;
                box-shadow: 0 20px 40px rgba(0, 212, 255, 0.3);
            }
            
            .action-icon {
                font-size: 32px;
                margin-bottom: 16px;
                display: block;
            }
            
            .action-label {
                font-size: 16px;
                font-weight: 600;
                margin-bottom: 8px;
            }
            
            .action-description {
                font-size: 12px;
                opacity: 0.8;
                line-height: 1.4;
            }
            
            /* Journal Interface */
            .journal-interface {
                background: rgba(255, 255, 255, 0.02);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 16px;
                padding: 32px;
                margin-bottom: 32px;
                text-align: left;
                opacity: 0;
                transform: translateY(-20px);
                transition: all 0.3s ease;
                display: none;
            }
            
            .journal-interface.active {
                opacity: 1;
                transform: translateY(0);
                display: block;
            }
            
            .journal-header {
                text-align: center;
                margin-bottom: 24px;
            }
            
            .journal-title {
                font-size: 20px;
                font-weight: 600;
                margin-bottom: 8px;
                color: #ffffff;
            }
            
            .journal-prompt {
                font-size: 14px;
                color: rgba(255, 255, 255, 0.8);
                font-style: italic;
            }
            
            .journal-input {
                width: 100%;
                min-height: 150px;
                background: rgba(255, 255, 255, 0.02);
                border: 1px solid rgba(255, 255, 255, 0.2);
                border-radius: 12px;
                padding: 20px;
                color: #ffffff;
                font-family: inherit;
                font-size: 16px;
                line-height: 1.6;
                resize: vertical;
                transition: all 0.3s ease;
            }
            
            .journal-input:focus {
                outline: none;
                border-color: #00d4ff;
                background: rgba(255, 255, 255, 0.05);
                box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1);
            }
            
            .journal-input::placeholder {
                color: rgba(255, 255, 255, 0.5);
                font-style: italic;
            }
            
            .journal-controls {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-top: 16px;
            }
            
            .character-count {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.6);
            }
            
            .journal-buttons {
                display: flex;
                gap: 12px;
            }
            
            .final-actions {
                display: flex;
                gap: 20px;
                justify-content: center;
            }
            
            /* Animations */
            @keyframes coreProcessor {
                0%, 100% { 
                    box-shadow: 0 0 40px rgba(0, 212, 255, 0.6), 0 0 80px rgba(0, 212, 255, 0.3);
                    transform: translate(-50%, -50%) scale(1);
                }
                50% { 
                    box-shadow: 0 0 60px rgba(0, 212, 255, 0.8), 0 0 120px rgba(0, 212, 255, 0.4);
                    transform: translate(-50%, -50%) scale(1.02);
                }
            }
            
            @keyframes ringRotate {
                from { transform: translate(-50%, -50%) rotate(0deg); }
                to { transform: translate(-50%, -50%) rotate(360deg); }
            }
            
            @keyframes phaseSequence {
                0%, 25% { opacity: 0; transform: translateY(10px); }
                30%, 70% { opacity: 1; transform: translateY(0); }
                75%, 100% { opacity: 0; transform: translateY(-10px); }
            }
            
            /* Mobile-specific animation adjustments for seamless experience */
            @media (max-width: 768px), (hover: none) {
                .core-processor {
                    animation: coreProcessor 8s ease-in-out infinite !important;
                }
                .ring-outer {
                    animation: ringRotate 90s linear infinite !important;
                }
                .ring-middle {
                    animation: ringRotate 70s linear infinite reverse !important;
                }
                .ring-inner {
                    animation: ringRotate 50s linear infinite !important;
                }
                .phase-text {
                    animation: phaseSequence 20s ease-in-out infinite !important;
                }
                .phase-text:nth-child(2) { animation-delay: 5s !important; }
                .phase-text:nth-child(3) { animation-delay: 10s !important; }
                .phase-text:nth-child(4) { animation-delay: 15s !important; }
            }
        `;
        
        document.head.appendChild(style);
    }
    
    showDOMStep(step) {
        const contentDiv = document.querySelector("#philosophy-flow-overlay #philosophy-flow-content");
        if (!contentDiv) return;
        
        console.log(`🎭 Showing DOM step ${step}`);
        
        switch (step) {
            case 1:
                this.showFeelingStep(contentDiv);
                break;
            case 2:
                this.showColorStep(contentDiv);
                break;
            case 3:
                this.showSleepStep(contentDiv);
                break;
            case 4:
                this.showLoadingStep(contentDiv);
                break;
            case 5:
                this.showQuoteStep(contentDiv);
                break;
            case 6:
                this.showRateLimitedStep(contentDiv);
                break;
        }
    }
    
    showFeelingStep(contentDiv) {
        // Step 1a: Category Selection (exactly from Part 1 mockup)
        contentDiv.innerHTML = `
            <div class="step-title">
                <div class="step-number">Step 1 of 6</div>
                <h1 class="step-heading">Explore Your Inner Landscape</h1>
                <p class="step-subtitle">Choose the category that best represents your current emotional territory</p>
            </div>
            
            <div class="category-grid">
                <div class="category-card" data-category="cognitive">
                    <span class="category-icon">🧠</span>
                    <div class="category-label">Cognitive States</div>
                    <div class="category-description">Mental clarity, thinking patterns, and cognitive processes</div>
                    <div class="category-examples">Analytical • Creative • Focused • Curious</div>
                </div>
                
                <div class="category-card" data-category="uplifting">
                    <span class="category-icon">🌟</span>
                    <div class="category-label">Uplifting States</div>
                    <div class="category-description">Positive emotions and elevated feelings</div>
                    <div class="category-examples">Joyful • Calm • Hopeful • Excited</div>
                </div>
                
                <div class="category-card" data-category="challenging">
                    <span class="category-icon">⚡</span>
                    <div class="category-label">Challenging States</div>
                    <div class="category-description">Difficult emotions that signal important needs</div>
                    <div class="category-examples">Angry • Frustrated • Worried • Disappointed</div>
                </div>
                
                <div class="category-card" data-category="heavy">
                    <span class="category-icon">🌑</span>
                    <div class="category-label">Heavy States</div>
                    <div class="category-description">Deep, intense emotions requiring gentle attention</div>
                    <div class="category-examples">Sad • Lonely • Empty • Lost</div>
                </div>
                
                <div class="category-card" data-category="energy">
                    <span class="category-icon">🔋</span>
                    <div class="category-label">Energy States</div>
                    <div class="category-description">Physical and emotional energy levels</div>
                    <div class="category-examples">Energized • Balanced • Tired • Drained</div>
                </div>
                
                <div class="category-card" data-category="reflective">
                    <span class="category-icon">🤔</span>
                    <div class="category-label">Reflective States</div>
                    <div class="category-description">Contemplative and introspective moods</div>
                    <div class="category-examples">Philosophical • Searching • Meditative • Contemplative</div>
                </div>
            </div>
        `;
        
        this.bindCategoryEvents();
    }
    
    bindCategoryEvents() {
        const categoryCards = document.querySelectorAll('#philosophy-flow-overlay .category-card');
        categoryCards.forEach(card => {
            card.addEventListener('click', () => {
                // Remove previous selection
                categoryCards.forEach(c => c.classList.remove('selected'));
                
                // Select current category
                card.classList.add('selected');
                this.selectedCategory = card.dataset.category;
                
                console.log(`🎭 Selected category: ${this.selectedCategory}`);
                
                // Show notification and proceed to emotion selection
                this.time.delayedCall(800, () => {
                    this.showEmotionSelection(this.selectedCategory);
                });
            });
        });
    }
    
    showEmotionSelection(categoryKey) {
        // Step 1b: Specific emotion selection within category
        const emotionCategories = {
            cognitive: {
                title: "Cognitive States",
                subtitle: "Select how your mind is processing right now",
                emotions: [
                    { name: 'Analytical', icon: '🧠', description: 'Clear logical thinking' },
                    { name: 'Creative', icon: '🎨', description: 'Innovative mindset' },
                    { name: 'Focused', icon: '🎯', description: 'Concentrated attention' },
                    { name: 'Curious', icon: '🔍', description: 'Open to learning' },
                    { name: 'Confused', icon: '😵‍💫', description: 'Unclear or uncertain' },
                    { name: 'Overwhelmed', icon: '🤯', description: 'Too much to process' }
                ]
            },
            uplifting: {
                title: "Uplifting States",
                subtitle: "Select your current positive emotional state",
                emotions: [
                    { name: 'Calm', icon: '🧘', description: 'Inner peace and balance' },
                    { name: 'Joyful', icon: '😊', description: 'Positive and uplifted' },
                    { name: 'Grateful', icon: '🙏', description: 'Appreciative mindset' },
                    { name: 'Excited', icon: '🤩', description: 'Enthusiastic anticipation' },
                    { name: 'Hopeful', icon: '🌟', description: 'Optimistic about future' },
                    { name: 'Proud', icon: '💪', description: 'Satisfied with achievement' }
                ]
            },
            challenging: {
                title: "Challenging States",
                subtitle: "Select the difficult emotion you're experiencing",
                emotions: [
                    { name: 'Angry', icon: '😠', description: 'Feeling upset or irritated' },
                    { name: 'Frustrated', icon: '😤', description: 'Blocked or hindered' },
                    { name: 'Embarrassed', icon: '😳', description: 'Self-conscious discomfort' },
                    { name: 'Disappointed', icon: '😞', description: 'Unmet expectations' },
                    { name: 'Worried', icon: '😰', description: 'Anxious about outcomes' },
                    { name: 'Guilty', icon: '😔', description: 'Regret over actions' }
                ]
            },
            heavy: {
                title: "Heavy States",
                subtitle: "Select the deep emotion that needs gentle attention",
                emotions: [
                    { name: 'Sad', icon: '😢', description: 'Deep emotional pain' },
                    { name: 'Lonely', icon: '😞', description: 'Feeling isolated' },
                    { name: 'Lost', icon: '🌫️', description: 'Unclear direction' },
                    { name: 'Empty', icon: '🕳️', description: 'Feeling hollow inside' },
                    { name: 'Numb', icon: '😶', description: 'Disconnected from feelings' },
                    { name: 'Dark', icon: '🌑', description: 'Heavy, shadowed mood' }
                ]
            },
            energy: {
                title: "Energy States",
                subtitle: "Select your current energy and vitality level",
                emotions: [
                    { name: 'Energized', icon: '⚡', description: 'High vitality' },
                    { name: 'Balanced', icon: '⚖️', description: 'Stable energy' },
                    { name: 'Tired', icon: '😴', description: 'Need for rest' },
                    { name: 'Restless', icon: '🌊', description: 'Seeking direction' },
                    { name: 'Drained', icon: '🔋', description: 'Emotionally depleted' },
                    { name: 'Burnt Out', icon: '🔥', description: 'Exhausted from stress' }
                ]
            },
            reflective: {
                title: "Reflective States",
                subtitle: "Select your contemplative or introspective mood",
                emotions: [
                    { name: 'Reflective', icon: '🤔', description: 'Contemplative mood' },
                    { name: 'Introspective', icon: '🌱', description: 'Looking inward' },
                    { name: 'Contemplative', icon: '🧙‍♂️', description: 'Deep philosophical mood' },
                    { name: 'Searching', icon: '🔦', description: 'Seeking answers' },
                    { name: 'Philosophical', icon: '📚', description: 'Questioning existence' },
                    { name: 'Meditative', icon: '🕯️', description: 'Centered awareness' }
                ]
            }
        };
        
        const categoryData = emotionCategories[categoryKey];
        const contentDiv = document.querySelector("#philosophy-flow-overlay #philosophy-flow-content");
        
        contentDiv.innerHTML = `
            <div class="step-title">
                <div class="step-number">Step 2 of 6</div>
                <h1 class="step-heading">${categoryData.title}</h1>
                <p class="step-subtitle">${categoryData.subtitle}</p>
            </div>
            
            <div class="selected-emotions-container">
                <div class="category-emotions-grid">
                    ${categoryData.emotions.map(emotion => `
                        <div class="emotion-card" data-emotion="${emotion.name}" data-icon="${emotion.icon}">
                            <span class="emotion-icon">${emotion.icon}</span>
                            <div class="emotion-label">${emotion.name}</div>
                            <div class="emotion-description">${emotion.description}</div>
                        </div>
                    `).join('')}
                </div>
            </div>
            
            <div style="text-align: center; margin-top: 40px;">
                <button class="executive-button" onclick="this.showDOMStep(1)" style="margin-right: 20px;">
                    ← Back to Categories
                </button>
                <button class="executive-button primary" id="confirmEmotion" disabled>
                    Continue with Selected State
                </button>
            </div>
        `;
        
        this.bindEmotionEvents();
    }
    
    bindEmotionEvents() {
        const emotionCards = document.querySelectorAll('#philosophy-flow-overlay .emotion-card');
        emotionCards.forEach(card => {
            card.addEventListener('click', () => {
                // Remove previous selection
                emotionCards.forEach(c => c.classList.remove('selected'));
                
                // Select current
                card.classList.add('selected');
                this.selectedEmotion = {
                    name: card.dataset.emotion,
                    icon: card.dataset.icon
                };
                
                // Enable confirm button
                const confirmBtn = document.getElementById('confirmEmotion');
                if (confirmBtn) {
                    confirmBtn.disabled = false;
                }
                
                console.log(`🎭 Selected emotion: ${this.selectedEmotion.name}`);
            });
        });
        
        const confirmBtn = document.getElementById('confirmEmotion');
        if (confirmBtn) {
            confirmBtn.addEventListener('click', () => {
                if (this.selectedEmotion) {
                    this.showDOMStep(2);
                }
            });
        }
    }

    showColorStep(contentDiv) {
        contentDiv.innerHTML = `
            <div class="step-title">
                <div class="step-number">Step 3 of 6</div>
                <h1 class="step-heading">Strategic Resonance</h1>
                <p class="step-subtitle">Select the color that represents your current strategic outlook</p>
            </div>
            
            <div class="color-analysis-section">
                <div class="executive-color-grid">
                    <div class="color-option" data-color="#FF3333" data-name="Passion" data-insight="Drive for achievement and bold decision-making">
                        <div class="color-swatch" style="background: #FF3333;"></div>
                        <div class="color-name">Passion</div>
                    </div>
                    <div class="color-option" data-color="#FFAA00" data-name="Innovation" data-insight="Creative problem-solving and strategic optimism">
                        <div class="color-swatch" style="background: #FFAA00;"></div>
                        <div class="color-name">Innovation</div>
                    </div>
                    <div class="color-option" data-color="#FFDD55" data-name="Vision" data-insight="Forward-thinking leadership and positive outlook">
                        <div class="color-swatch" style="background: #FFDD55;"></div>
                        <div class="color-name">Vision</div>
                    </div>
                    <div class="color-option" data-color="#AAFF55" data-name="Growth" data-insight="Expansion mindset and dynamic execution">
                        <div class="color-swatch" style="background: #AAFF55;"></div>
                        <div class="color-name">Growth</div>
                    </div>
                    <div class="color-option" data-color="#55FF88" data-name="Balance" data-insight="Steady leadership and measured decision-making">
                        <div class="color-swatch" style="background: #55FF88;"></div>
                        <div class="color-name">Balance</div>
                    </div>
                    <div class="color-option" data-color="#55FFFF" data-name="Clarity" data-insight="Clear strategic thinking and transparent communication">
                        <div class="color-swatch" style="background: #55FFFF;"></div>
                        <div class="color-name">Clarity</div>
                    </div>
                    <div class="color-option" data-color="#5588FF" data-name="Reflection" data-insight="Thoughtful analysis and strategic contemplation">
                        <div class="color-swatch" style="background: #5588FF;"></div>
                        <div class="color-name">Reflection</div>
                    </div>
                    <div class="color-option" data-color="#AA55FF" data-name="Creativity" data-insight="Creative leadership and transformational thinking">
                        <div class="color-swatch" style="background: #AA55FF;"></div>
                        <div class="color-name">Creativity</div>
                    </div>
                    <div class="color-option" data-color="#FF88CC" data-name="Empathy" data-insight="People-centered leadership and emotional intelligence">
                        <div class="color-swatch" style="background: #FF88CC;"></div>
                        <div class="color-name">Empathy</div>
                    </div>
                    <div class="color-option" data-color="#F8F9FA" data-name="Purity" data-insight="Principled leadership and ethical decision-making">
                        <div class="color-swatch" style="background: #F8F9FA; border: 2px solid rgba(0, 212, 255, 0.3);"></div>
                        <div class="color-name">Purity</div>
                    </div>
                    <div class="color-option" data-color="#808080" data-name="Stability" data-insight="Consistent performance and reliable execution">
                        <div class="color-swatch" style="background: #808080;"></div>
                        <div class="color-name">Stability</div>
                    </div>
                    <div class="color-option" data-color="#1A1A1A" data-name="Authority" data-insight="Commanding presence and decisive leadership">
                        <div class="color-swatch" style="background: #1A1A1A;"></div>
                        <div class="color-name">Authority</div>
                    </div>
                </div>
                
                <div class="color-feedback">
                    <div class="selected-color-display" id="colorDisplay" style="background: #FF3333;"></div>
                    <div class="mood-name" id="moodName">Passion</div>
                    <div class="mood-insight" id="moodInsight">Drive for achievement and bold decision-making</div>
                </div>
            </div>
            
            <div style="text-align: center; margin-top: 60px;">
                <button class="executive-button primary" id="confirmColor">
                    Confirm Strategic Color
                </button>
            </div>
        `;
        
        this.bindColorEvents();
    }

    showSleepStep(contentDiv) {
        contentDiv.innerHTML = `
            <div class="step-title">
                <div class="step-number">Step 4 of 6</div>
                <h1 class="step-heading">Recovery Assessment</h1>
                <p class="step-subtitle">How much sleep did you achieve last night?</p>
            </div>
            
            <div class="recovery-section">
                <div class="sleep-options">
                    <div class="sleep-option" data-sleep="< 5 hours">
                        <div class="sleep-content">
                            <div class="sleep-duration">< 5 hours</div>
                            <div class="sleep-description">High performance mode, minimal recovery</div>
                        </div>
                        <div class="sleep-indicator">⚠️</div>
                    </div>
                    <div class="sleep-option" data-sleep="5-6 hours">
                        <div class="sleep-content">
                            <div class="sleep-duration">5-6 hours</div>
                            <div class="sleep-description">Standard executive schedule</div>
                        </div>
                        <div class="sleep-indicator">⏰</div>
                    </div>
                    <div class="sleep-option" data-sleep="6-7 hours">
                        <div class="sleep-content">
                            <div class="sleep-duration">6-7 hours</div>
                            <div class="sleep-description">Balanced performance recovery</div>
                        </div>
                        <div class="sleep-indicator">📊</div>
                    </div>
                    <div class="sleep-option" data-sleep="7-8 hours">
                        <div class="sleep-content">
                            <div class="sleep-duration">7-8 hours</div>
                            <div class="sleep-description">Optimal executive wellness</div>
                        </div>
                        <div class="sleep-indicator">✅</div>
                    </div>
                    <div class="sleep-option" data-sleep="> 8 hours">
                        <div class="sleep-content">
                            <div class="sleep-duration">> 8 hours</div>
                            <div class="sleep-description">Extended recovery period</div>
                        </div>
                        <div class="sleep-indicator">😴</div>
                    </div>
                </div>
            </div>
        `;
        
        this.bindSleepEvents();
    }

    showLoadingStep(contentDiv) {
        contentDiv.innerHTML = `
            <div class="step-title">
                <div class="step-number">Step 5 of 6</div>
                <h1 class="step-heading">Executive Wisdom Synthesis</h1>
                <p class="step-subtitle">Analyzing your profile against centuries of leadership wisdom</p>
            </div>
            
            <div class="synthesis-container">
                <div class="wisdom-engine">
                    <div class="central-processor"></div>
                    <div class="knowledge-ring ring-outer"></div>
                    <div class="knowledge-ring ring-middle"></div>
                    <div class="knowledge-ring ring-inner"></div>
                </div>
                
                <div class="synthesis-status">
                    <div class="status-title">Processing Executive Intelligence</div>
                    <div class="phase-text">Analyzing emotional resonance patterns...</div>
                    <div class="phase-text">Cross-referencing strategic frameworks...</div>
                    <div class="phase-text">Calibrating recovery optimization...</div>
                    <div class="phase-text">Synthesizing personalized wisdom...</div>
                </div>
            </div>
        `;
        
        // Non-blocking quote generation with immediate fallback
        this.generateQuoteForDOM().then(() => {
            // Wait for animation to complete, then show quote (mobile-aware timing)
            this.time.delayedCall(ANIMATION_WAIT_TIME, () => {
                this.showDOMStep(5);
            });
        }).catch(() => {
            // Immediate fallback if quote generation fails
            this.quoteData = {
                quote: "The obstacle is the way.",
                figure: "Marcus Aurelius",
                reasoning: "Universal wisdom for overcoming challenges."
            };
            this.time.delayedCall(ANIMATION_WAIT_TIME, () => {
                this.showDOMStep(5);
            });
        });
    }

    showQuoteStep(contentDiv) {
        if (!this.quoteData) {
            this.showDOMStep(4); // Go back to loading if no quote
            return;
        }
        
        contentDiv.innerHTML = `
            <div class="step-title">
                <div class="step-number">Step 6 of 6</div>
                <h1 class="step-heading">Executive Wisdom</h1>
                <p class="step-subtitle">Your personalized insight for strategic leadership</p>
            </div>
            
            <div class="quote-presentation">
                <div class="quote-content">
                    <div class="quote-text">"${this.quoteData.quote}"</div>
                    <div class="quote-attribution">— ${this.quoteData.figure}</div>
                </div>
                
                <div class="insight-panel">
                    <div class="insight-title">Strategic Reasoning</div>
                    <div class="insight-text">${this.quoteData.reasoning}</div>
                </div>
                
                <div class="executive-actions">
                    <div class="action-card" id="favoriteAction">
                        <span class="action-icon">⭐</span>
                        <div class="action-label">Strategic Favorite</div>
                        <div class="action-description">Add to executive collection</div>
                    </div>
                    <div class="action-card" id="journalAction">
                        <span class="action-icon">📋</span>
                        <div class="action-label">Executive Notes</div>
                        <div class="action-description">Document strategic insights</div>
                    </div>
                    <div class="action-card" id="saveAction">
                        <span class="action-icon">💾</span>
                        <div class="action-label">Wisdom Library</div>
                        <div class="action-description">Archive for future reference</div>
                    </div>
                </div>
                
                <div class="journal-interface" id="journalInterface">
                    <div class="journal-header">
                        <div class="journal-title">Executive Reflection</div>
                        <div class="journal-prompt">How will you apply this wisdom to your strategic initiatives?</div>
                    </div>
                    <textarea class="journal-input" id="executiveJournal" placeholder="Document your strategic insights, implementation plans, or how this wisdom relates to current initiatives..." maxlength="500"></textarea>
                    <div class="journal-controls">
                        <div class="character-count">
                            <span id="charCount">0</span>/500 characters
                        </div>
                        <div class="journal-buttons">
                            <button class="executive-button" id="cancelJournal">Cancel</button>
                            <button class="executive-button primary" id="saveJournal">Save Strategic Note</button>
                        </div>
                    </div>
                </div>
                
                <div class="final-actions">
                    <button class="executive-button" id="discardSession">Discard Session</button>
                    <button class="executive-button primary" id="completeSession">Complete Session</button>
                </div>
            </div>
        `;
        
        this.bindQuoteEvents();
    }
    
    bindQuoteEvents() {
        // Favorite action
        const favoriteAction = document.getElementById('favoriteAction');
        if (favoriteAction) {
            favoriteAction.addEventListener('click', () => this.toggleFavorite());
        }
        
        // Journal action
        const journalAction = document.getElementById('journalAction');
        if (journalAction) {
            journalAction.addEventListener('click', () => this.openExecutiveJournal());
        }
        
        // Save action
        const saveAction = document.getElementById('saveAction');
        if (saveAction) {
            saveAction.addEventListener('click', () => this.saveToLibrary());
        }
        
        // Journal controls
        const journalInput = document.getElementById('executiveJournal');
        if (journalInput) {
            journalInput.addEventListener('input', () => this.updateCharacterCount());
        }
        
        const cancelJournal = document.getElementById('cancelJournal');
        if (cancelJournal) {
            cancelJournal.addEventListener('click', () => this.cancelJournal());
        }
        
        const saveJournal = document.getElementById('saveJournal');
        if (saveJournal) {
            saveJournal.addEventListener('click', () => this.saveJournal());
        }
        
        // Final actions
        const discardSession = document.getElementById('discardSession');
        if (discardSession) {
            discardSession.addEventListener('click', () => this.discardSession());
        }
        
        const completeSession = document.getElementById('completeSession');
        if (completeSession) {
            completeSession.addEventListener('click', () => this.completeSession());
        }
    }
    
    toggleFavorite() {
        const action = document.getElementById('favoriteAction');
        
        if (this.isFavorited) {
            action.classList.remove('active');
            action.querySelector('.action-label').textContent = 'Strategic Favorite';
            this.isFavorited = false;
        } else {
            action.classList.add('active');
            action.querySelector('.action-label').textContent = 'Favorited';
            this.isFavorited = true;
        }
        
        console.log(`🎭 Favorite toggled: ${this.isFavorited}`);
    }
    
    openExecutiveJournal() {
        const journalInterface = document.getElementById('journalInterface');
        const action = document.getElementById('journalAction');
        
        if (journalInterface.classList.contains('active')) {
            this.cancelJournal();
        } else {
            journalInterface.classList.add('active');
            action.classList.add('active');
            action.querySelector('.action-label').textContent = 'Writing...';
            document.getElementById('executiveJournal').focus();
        }
    }
    
    saveToLibrary() {
        const action = document.getElementById('saveAction');
        
        if (!this.isSaved) {
            action.classList.add('active');
            action.querySelector('.action-label').textContent = 'Archived';
            this.isSaved = true;
            console.log('🎭 Quote saved to library');
        }
    }
    
    cancelJournal() {
        const journalInterface = document.getElementById('journalInterface');
        const action = document.getElementById('journalAction');
        
        journalInterface.classList.remove('active');
        action.classList.remove('active');
        action.querySelector('.action-label').textContent = 'Executive Notes';
        document.getElementById('executiveJournal').value = '';
        this.updateCharacterCount();
    }
    
    saveJournal() {
        const input = document.getElementById('executiveJournal');
        const action = document.getElementById('journalAction');
        
        if (!input || input.value.trim().length === 0) {
            alert('Please document your strategic insights before saving');
            return;
        }
        
        this.executiveNote = input.value.trim();
        action.querySelector('.action-label').textContent = 'Note Saved';
        console.log('🎭 Executive note saved');
        
        setTimeout(() => {
            this.cancelJournal();
        }, 1500);
    }
    
    updateCharacterCount() {
        const input = document.getElementById('executiveJournal');
        const count = document.getElementById('charCount');
        
        if (input && count) {
            count.textContent = input.value.length;
        }
    }
    
    async saveQuoteToFirebase() {
        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();
        
        if (!user || (!user.emailVerified && !specialAccess)) {
            console.log('🎭 User not authenticated or verified, skipping Firebase save');
            
            // Show themed notification for guest users
            if (!user) {
                this.showGuestSaveNotification();
            }
            return false;
        }

        try {
            // Validate required data before saving
            if (!this.quoteData || !this.quoteData.quote || !this.quoteData.figure) {
                throw new Error('Invalid quote data - missing required fields');
            }

            const quoteDoc = {
                userId: user.uid,
                userEmail: user.email,
                quote: this.quoteData.quote,
                figure: this.quoteData.figure,
                reasoning: this.quoteData.reasoning || '',
                selectedEmotion: this.selectedEmotion || null,
                selectedColor: this.selectedColor || null,
                selectedSleep: this.selectedSleep || null,
                favorited: Boolean(this.isFavorited),
                saved: Boolean(this.isSaved),
                executiveNote: this.executiveNote || '',
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                sessionType: 'Philosophy Flow',
                version: '33.0.0'
            };

            console.log('🎭 Saving quote to Firebase:', quoteDoc);
            const docRef = await db.collection('philosophyQuotes').add(quoteDoc);
            console.log('🎭 Quote saved to Firebase successfully:', docRef.id);
            return true;
        } catch (error) {
            console.error('🎭 Error saving quote to Firebase:', error);
            console.error('🎭 Error code:', error.code);
            console.error('🎭 Error message:', error.message);
            
            // Provide specific error feedback
            if (error.code === 'permission-denied') {
                alert('Permission denied: Unable to save quote. Please ensure you are signed in.');
            } else if (error.code === 'failed-precondition') {
                alert('Database error: Please try again in a moment.');
            } else {
                alert('Unable to save quote. Please check your connection and try again.');
            }
            return false;
        }
    }
    
    showGuestSaveNotification() {
        // Simple, enterprise-grade notification
        console.log('🎭 Guest user attempted to save - showing signup encouragement');
        alert('Sign up to save your philosophy insights permanently!\n\nUnlock your personal wisdom collection and get tailored recommendations.');
    }
    
    discardSession() {
        if (confirm('Discard this executive wisdom session? All insights will be lost.')) {
            console.log('🎭 Session discarded');
            // Return to previous scene if available, otherwise use MenuRouting
            const previousScene = sceneManager.previousSceneKey;
            if (previousScene && previousScene !== 'PhilosophyFlowScene') {
                sceneManager.switchToScene(previousScene);
            } else {
                // Fallback to appropriate menu
                import('../utils/MenuRouting.js').then(({ default: MenuRouting }) => {
                    const targetScene = MenuRouting.determineMenuScene();
                    sceneManager.switchToScene(targetScene);
                });
            }
        }
    }
    
    async completeSession() {
        console.log('🎭 Philosophy Flow session completing...');
        
        const user = auth.currentUser;
        const isGuest = !user || !user.emailVerified;
        const specialAccess = hasSpecialAccess();
        
        try {
            // Save to Firebase if user interacted with the quote AND is authenticated
            if (this.isFavorited || this.isSaved || this.executiveNote) {
                if (isGuest && !specialAccess) {
                    // Show guest save notification instead of attempting save
                    this.showGuestSaveNotification();
                } else {
                    const saveSuccess = await this.saveQuoteToFirebase();
                    if (saveSuccess) {
                        // Invalidate cache to ensure fresh data in Profile
                        if (window.userDataCacheService) {
                            window.userDataCacheService.clearCache('philosophy-flow-session-complete');
                        }
                        console.log('🎭 Philosophy Flow session saved and cache invalidated');
                    }
                }
            }
            
            // Clean up DOM elements
            const overlay = document.getElementById('philosophy-flow-overlay');
            if (overlay) {
                overlay.remove();
            }
            
            // Clean up scene references
            this.selectedEmotion = null;
            this.selectedColor = null;
            this.selectedSleep = null;
            this.quoteData = null;
            this.executiveNote = '';
            
        } catch (error) {
            console.error('🎭 Error during session completion:', error);
        } finally {
            console.log('🎭 Philosophy Flow session completed');
            // Return to previous scene if available, otherwise use MenuRouting
            const previousScene = sceneManager.previousSceneKey;
            if (previousScene && previousScene !== 'PhilosophyFlowScene') {
                sceneManager.switchToScene(previousScene);
            } else {
                // Fallback to appropriate menu
                import('../utils/MenuRouting.js').then(({ default: MenuRouting }) => {
                    const targetScene = MenuRouting.determineMenuScene();
                    sceneManager.switchToScene(targetScene);
                });
            }
        }
    }
    

    showRateLimitedStep(contentDiv) {
        contentDiv.innerHTML = `
            <div class="philosophy-step">
                <h2 class="step-title">Wisdom Dispensed for Today</h2>
                <div style="text-align: center;">
                    <div style="font-size: 18px; color: rgba(255, 255, 255, 0.9); margin-bottom: 24px;">You've received your daily dose of wisdom. Philosophy Flow encourages mindful reflection.</div>
                    <div style="font-size: 16px; color: #00d4ff; font-weight: 600; margin-bottom: 32px;">Please return after 24 hours for your next philosophical journey.</div>
                    <button class="primary-button" onclick="document.getElementById('philosophy-flow-overlay').remove(); sceneManager.switchToScene('AuthenticatedMenuSceneDOM');">BACK TO MENU</button>
                </div>
            </div>
        `;
    }

    bindColorEvents() {
        const colorOptions = document.querySelectorAll('#philosophy-flow-overlay .color-option');
        colorOptions.forEach(option => {
            option.addEventListener('click', () => {
                // Remove previous selection
                colorOptions.forEach(opt => opt.classList.remove('selected'));
                
                // Select current color
                option.classList.add('selected');
                
                const colorData = {
                    color: option.dataset.color,
                    name: option.dataset.name,
                    insight: option.dataset.insight
                };
                
                // Update displays
                const colorDisplay = document.getElementById('colorDisplay');
                const moodName = document.getElementById('moodName');
                const moodInsight = document.getElementById('moodInsight');
                
                if (colorDisplay) colorDisplay.style.background = colorData.color;
                if (moodName) moodName.textContent = colorData.name;
                if (moodInsight) moodInsight.textContent = colorData.insight;
                
                this.selectedColor = colorData;
                console.log(`🎭 Selected color: ${colorData.name} (${colorData.color})`);
            });
        });

        const confirmBtn = document.getElementById('confirmColor');
        if (confirmBtn) {
            confirmBtn.addEventListener('click', () => {
                if (this.selectedColor) {
                    this.showDOMStep(3);
                }
            });
        }
    }
    
    selectExecutiveColor(event) {
        const wheel = event.currentTarget;
        const rect = wheel.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        const x = event.clientX - centerX;
        const y = event.clientY - centerY;
        
        let angle = Math.atan2(y, x) * 180 / Math.PI;
        if (angle < 0) angle += 360;
        
        // Executive color mappings
        const executiveColorMap = [
            { color: '#FF3333', name: 'Passion', insight: 'Drive for achievement and bold decision-making', angle: 0 },
            { color: '#FFAA00', name: 'Innovation', insight: 'Creative problem-solving and strategic optimism', angle: 30 },
            { color: '#FFDD55', name: 'Vision', insight: 'Forward-thinking leadership and positive outlook', angle: 60 },
            { color: '#AAFF55', name: 'Growth', insight: 'Expansion mindset and dynamic execution', angle: 90 },
            { color: '#55FF88', name: 'Balance', insight: 'Steady leadership and measured decision-making', angle: 120 },
            { color: '#55FFFF', name: 'Clarity', insight: 'Clear strategic thinking and transparent communication', angle: 150 },
            { color: '#5588FF', name: 'Reflection', insight: 'Thoughtful analysis and strategic contemplation', angle: 180 },
            { color: '#AA55FF', name: 'Innovation', insight: 'Creative leadership and transformational thinking', angle: 210 },
            { color: '#FF88CC', name: 'Empathy', insight: 'People-centered leadership and emotional intelligence', angle: 240 },
            { color: '#FFFFFF', name: 'Purity', insight: 'Principled leadership and ethical decision-making', angle: 270 },
            { color: '#808080', name: 'Stability', insight: 'Consistent performance and reliable execution', angle: 300 },
            { color: '#1A1A1A', name: 'Authority', insight: 'Commanding presence and decisive leadership', angle: 330 }
        ];
        
        // Find closest color
        let closestColor = executiveColorMap[0];
        let minDiff = Math.abs(angle - closestColor.angle);
        
        executiveColorMap.forEach(colorData => {
            const diff = Math.min(
                Math.abs(angle - colorData.angle),
                Math.abs(angle - colorData.angle + 360),
                Math.abs(angle - colorData.angle - 360)
            );
            if (diff < minDiff) {
                minDiff = diff;
                closestColor = colorData;
            }
        });
        
        // Update selector position
        const selectorAngle = closestColor.angle * Math.PI / 180;
        const radius = 160; // Half of 320px wheel
        const selectorX = Math.cos(selectorAngle) * radius + 160;
        const selectorY = Math.sin(selectorAngle) * radius + 160;
        
        const selector = document.getElementById('colorSelector');
        if (selector) {
            selector.style.left = selectorX + 'px';
            selector.style.top = selectorY + 'px';
        }
        
        // Update displays
        const colorDisplay = document.getElementById('colorDisplay');
        const moodName = document.getElementById('moodName');
        const moodInsight = document.getElementById('moodInsight');
        
        if (colorDisplay) colorDisplay.style.background = closestColor.color;
        if (moodName) moodName.textContent = closestColor.name;
        if (moodInsight) moodInsight.textContent = closestColor.insight;
        
        this.selectedColor = closestColor;
        console.log(`🎭 Selected color: ${closestColor.name} (${closestColor.color})`);
    }

    bindSleepEvents() {
        const sleepOptions = document.querySelectorAll('#philosophy-flow-overlay .sleep-option');
        sleepOptions.forEach(option => {
            option.addEventListener('click', () => {
                this.selectedSleep = option.dataset.sleep;
                console.log(`🎭 Selected sleep: ${this.selectedSleep}`);
                
                // Visual feedback
                sleepOptions.forEach(opt => opt.style.background = 'rgba(255, 255, 255, 0.05)');
                option.style.background = 'rgba(0, 212, 255, 0.1)';
                
                this.time.delayedCall(800, () => {
                    this.showDOMStep(4);
                });
            });
        });
    }

    async generateQuoteForDOM() {
        try {
            const emotionName = this.selectedEmotion?.name || 'Neutral';
            const colorName = this.selectedColor?.name || 'Calm';
            const sleepAmount = this.selectedSleep || '7-8 hours';
            
            const prompt = `Generate a philosophical quote based on: Emotion=${emotionName}, Color=${colorName}, Sleep=${sleepAmount}. The user is in a ${emotionName} state with ${colorName} strategic resonance and ${sleepAmount} of recovery. Format: QUOTE: [quote] FIGURE: [philosopher] REASONING: [reason]`;
            
            const response = await this.claudeService.callClaudeApiWithRetry(prompt, 250);
            const extractedText = this.claudeService.extractResponse(response);
            
            // Simple parsing
            const quoteMatch = extractedText.match(/QUOTE:\s*(.*?)\s*FIGURE:/i);
            const figureMatch = extractedText.match(/FIGURE:\s*(.*?)\s*REASONING:/i);
            const reasoningMatch = extractedText.match(/REASONING:\s*(.*?)$/i);

            this.quoteData = {
                quote: quoteMatch ? quoteMatch[1].trim().replace(/^"|"$/g, '') : "The obstacle is the way.",
                figure: figureMatch ? figureMatch[1].trim() : "Marcus Aurelius",
                reasoning: reasoningMatch ? reasoningMatch[1].trim() : "Universal wisdom for any situation."
            };
            
            console.log('🎭 Quote generated:', this.quoteData);
        } catch (error) {
            console.error('🎭 Quote generation failed:', error);
            this.quoteData = {
                quote: "The obstacle is the way.",
                figure: "Marcus Aurelius", 
                reasoning: "Universal wisdom for any situation."
            };
        }
    }

    // checkRateLimit and performNormalRateLimitCheck methods remain unchanged
    checkRateLimit() { if (!this.lastQuoteTimestamp) { this.isRateLimited = false; return; } if(this.isAdmin) { this.isRateLimited = false; return;} this.performNormalRateLimitCheck();}
    performNormalRateLimitCheck() { if (!this.lastQuoteTimestamp) { this.isRateLimited = false; return; } const now = new Date(); const lastQuoteDate = this.lastQuoteTimestamp.toDate(); const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000)); this.isRateLimited = lastQuoteDate > twentyFourHoursAgo; console.log(`Rate limit check: ${this.isRateLimited ? 'LIMITED' : 'OK'}.`);}
    updateAdminUI() { 
        if (this.currentStep === STEP_RATE_LIMITED && this.isAdmin) { 
            this.showStep(STEP_EMOJI); 
        } else if (this.isAdmin && !this.uiElements.adminBadge && this.scene.isActive()) { 
            // CRITICAL: Only create admin badge when scene is actually active
            const adminBadge = this.add.text( 10, 10, "ADMIN", { 
                fontFamily: 'monospace', 
                fontSize: '12px', 
                color: '#FF55FF', 
                backgroundColor: '#330033', 
                padding: { x: 5, y: 3 } 
            } ).setDepth(1000).setScrollFactor(0); 
            this.uiElements.adminBadge = adminBadge;
        } 
    }


    // --- Step UI Management ---
    clearStepUI() {
        return new Promise((resolve) => {
            if (this.isTransitioningSteps) {
                 console.warn(`[${this.scene.key}-${SCENE_INSTANCE_ID}] clearStepUI skipped (already transitioning).`);
                 resolve(); // Resolve immediately if already transitioning
                 return;
            }
            this.isTransitioningSteps = true; // Set flag
            console.log(`[${this.scene.key}-${SCENE_INSTANCE_ID}] Clearing step UI START (Container: ${this.stepContainer ? 'Exists' : 'Null'})...`);

            // Stop any active feedback tween
            if (this.feedbackTween && this.feedbackTween.isPlaying()) {
                 this.feedbackTween.stop();
                 this.feedbackTween = null;
            }
      // Finally, make sure we clean up the animation when changing steps by adding this to clearStepUI() method:
                // Place this inside the clearStepUI() function, before destroying the container:
                
                // Clean up animation update callback if it exists
// Clean up animation update callback if it exists
if (this.uiElements[`step_${this.currentStep}_animCallback`]) {
    this.events.off('update', this.uiElements[`step_${this.currentStep}_animCallback`]);
    delete this.uiElements[`step_${this.currentStep}_animCallback`];
}

// Clean up any additional UI elements we might have created
if (this.uiElements[`step_${this.currentStep}_reasoningPanel`]) {
    try {
        this.uiElements[`step_${this.currentStep}_reasoningPanel`].destroy();
    } catch(e){}
    delete this.uiElements[`step_${this.currentStep}_reasoningPanel`];
}
                // Clean up any active tweens
                ['hintTween'].forEach(tweenName => {
                    const tween = this.uiElements[`step_${this.currentStep}_${tweenName}`];
                    if (tween && tween.isPlaying) {
                        tween.stop();
                    }
                    delete this.uiElements[`step_${this.currentStep}_${tweenName}`];
                });
            // Clear specific wheel elements if they exist
            if (this.activeWheelSprite) { try { this.activeWheelSprite.destroy(); } catch(e){} this.activeWheelSprite = null; }
            if (this.wheelSelectorSprite) { try { this.wheelSelectorSprite.destroy(); } catch(e){} this.wheelSelectorSprite = null; }
            if (this.wheelColorDisplayRect) { try { this.wheelColorDisplayRect.destroy(); } catch(e){} this.wheelColorDisplayRect = null; }

            // Get reference to the container that needs destroying
            const containerToDestroy = this.stepContainer;
            this.stepContainer = null; // Nullify reference immediately

            // --- Clear step-specific UI element references ---
            // Including the selectedEmojiButton reference
            const keysToClear = Object.keys(this.uiElements).filter(key =>
                 key.startsWith('step_') ||
                 [ 'spinner', 'spinnerTween', 'quoteText', 'figureText', 'reasoningText',
                   'doneButton', 'rateLimitTitle', 'rateLimitInfo', 'rateLimitBackButton',
                   'errorTitle', 'errorMessage', 'errorBackButton',
                   'emojiGridContainer', 'selectedEmojiButton', // <-- Ensure selected button ref is cleared
                   'activeWheelSprite', 'wheelSelectorSprite', 'wheelColorDisplayRect' // Redundant clear, but safe
                 ].includes(key)
             );
             keysToClear.forEach(key => {
                 // Note: We don't destroy elements here, just clear the reference map.
                 // The actual objects are destroyed with the containerToDestroy.
                 delete this.uiElements[key];
             });
             console.log(`[${this.scene.key}-${SCENE_INSTANCE_ID}] Step UI references cleared.`);
            // --- End Clear ---


            // Fade out and destroy the old container
            if (containerToDestroy && containerToDestroy.scene && containerToDestroy.active) {
                console.log(`[${this.scene.key}-${SCENE_INSTANCE_ID}] Fading out previous step container...`);
                // Use scene's tweens if possible, fallback to game tweens
                const tweenManager = this.tweens || this.scene?.systems?.tweens;
                tweenManager?.add({
                    targets: containerToDestroy,
                    alpha: 0,
                    duration: FADE_DURATION,
                    ease: 'Power1',
                    onComplete: () => {
                        try {
                            // Double check if it wasn't already destroyed elsewhere
                            if (containerToDestroy.active) {
                                containerToDestroy.destroy(true); // Destroy container and children
                                console.log(`[${this.scene.key}-${SCENE_INSTANCE_ID}] Previous step container destroyed successfully after fade.`);
                            }
                        } catch (e) {
                            // Ignore errors if it was already destroyed
                            if (!e.message?.toLowerCase().includes('destroyed')) {
                                console.warn(`[${this.scene.key}-${SCENE_INSTANCE_ID}] Error destroying faded container:`, e);
                            }
                        } finally {
                             this.isTransitioningSteps = false; // Reset flag *after* completion
                             resolve(); // Resolve the promise
                        }
                    }
                });
            } else {
                console.log(`[${this.scene.key}-${SCENE_INSTANCE_ID}] No active step container found to fade out and destroy.`);
                 this.isTransitioningSteps = false; // Reset flag if no container
                 resolve(); // Resolve immediately
            }
        });
    }

    async showStep(stepNumber) {
         if (this.isTransitioningSteps) { console.warn(`showStep(${stepNumber}) skipped.`); return; }
         console.log(`[${this.scene.key}-${SCENE_INSTANCE_ID}] --- Showing Step ${stepNumber} ---`);
         await this.clearStepUI();
         if (!this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) { console.warn(`Scene inactive before showing step ${stepNumber}.`); return; }

         this.currentStep = stepNumber;
         this.isTransitioningSteps = true;
         try {
             this.stepContainer = this.add.container(0, 0).setAlpha(0);
             this.createStepUIElements(stepNumber); // Create elements
             this.positionStepUI(stepNumber); // Position them

             // Fade in animation
             if (this.stepContainer?.scene && this.tweens) {
                 this.tweens.add({
                     targets: this.stepContainer, alpha: 1, duration: FADE_DURATION, ease: 'Power1',
                     onComplete: () => { this.isTransitioningSteps = false; } // Reset flag after fade in
                 });
             } else { // Fallback if tweening isn't available
                 if(this.stepContainer) this.stepContainer.setAlpha(1);
                 this.isTransitioningSteps = false;
             }
         } catch (error) {
             console.error(`Error during showStep(${stepNumber}):`, error);
             if(this.stepContainer) { try { this.stepContainer.destroy(true); } catch(e){} this.stepContainer = null; }
             this.showMessage("Error displaying step.", 2000, true);
             this.isTransitioningSteps = false; // Reset flag on error
         }
    }

    // --- Create UI elements for each step ---
    createStepUIElements(stepNumber) {
        if (!this.stepContainer) return;
        let stepTitleText = "";

        // Create Title Text first if applicable
        switch (stepNumber) {
            case STEP_EMOJI: stepTitleText = "Tap the feeling that best represents you now:"; break;
            case STEP_COLOR: stepTitleText = "Tap the mood color that resonates most:"; break;
            case STEP_SLEEP: stepTitleText = "How much sleep did you get last night?"; break;
            case STEP_GENERATING: stepTitleText = "Generating wisdom..."; break;
            case STEP_RATE_LIMITED: stepTitleText = "Wisdom Dispensed for Today"; break;
            case STEP_DISPLAY_QUOTE: stepTitleText = "Your Moment of Philosophy"; break; // Title for quote display
        }
        if (stepTitleText) {
             // Use DeckOps design system colors - primary brand gradient text
             this.uiElements[`step_${stepNumber}_title`] = this.createThemedText(0, 0, stepTitleText, 'sizeLg', 0x00d4ff);
             if (this.uiElements[`step_${stepNumber}_title`]) { this.stepContainer.add(this.uiElements[`step_${stepNumber}_title`]); }
        }

        // Create step-specific elements
        switch (stepNumber) {
            case STEP_EMOJI:
                this.createEmojiGrid(EMOJI_FEELINGS_MAP); // Call grid creation helper
                this.uiElements[`step_${stepNumber}_confirmBtn`] = this.createStyledButton(0, 0, "Confirm Feeling", () => {
                    if (this.selectedFeeling) this.showStep(STEP_COLOR);
                    else this.showMessage("Please select a feeling.", 1500, true);
                }, 0x00d4ff); // DeckOps primary brand color
                this.stepContainer.add(this.uiElements[`step_${stepNumber}_confirmBtn`]);
                break;
            case STEP_COLOR:
                this.createWheelUI('color-wheel', 'mood');
                this.wheelColorDisplayRect = this.add.rectangle(0, 0, COLOR_DISPLAY_SIZE, COLOR_DISPLAY_SIZE, 0xcccccc)
                    .setStrokeStyle(2, 0xeeeeee);
                this.stepContainer.add(this.wheelColorDisplayRect);
                this.uiElements[`step_${stepNumber}_colorDisplay`] = this.wheelColorDisplayRect;
                this.uiElements[`step_${stepNumber}_confirmBtn`] = this.createStyledButton(0, 0, "Confirm Mood", () => {
                    if (this.selectedMood) this.showStep(STEP_SLEEP);
                    else this.showMessage("Please select a mood color.", 1500, true);
                }, 0x00d4ff); // DeckOps primary brand color
                this.stepContainer.add(this.uiElements[`step_${stepNumber}_confirmBtn`]);
                break;
            case STEP_SLEEP:
                const options = [ { value: '<5', display: '< 5 hours' }, { value: '5-6', display: '5-6 hours' }, { value: '6-7', display: '6-7 hours' }, { value: '7-8', display: '7-8 hours' }, { value: '8+', display: '> 8 hours' } ];
                options.forEach((option, index) => {
                     // Use DeckOps secondary colors for sleep options - rgba(255, 255, 255, 0.05) card background
                     const btn = this.createStyledButton(0, 0, option.display, () => { this.selectedSleep = option.value; this.generateQuote(); }, 0x334155, 200, 60);
                     if (btn) { this.stepContainer.add(btn); this.uiElements[`step_${stepNumber}_button_${index}`] = btn; }
                });
                break;
                // Replace the existing STEP_GENERATING case in createStepUIElements method
case STEP_GENERATING:
    stepTitleText = "Generating wisdom...";
    
    // Create a container for our animation elements
    const animContainer = this.add.container(0, 0);
    this.uiElements[`step_${stepNumber}_animContainer`] = animContainer;
    this.stepContainer.add(animContainer);
    
    // Create multiple orbiting particles
    const numParticles = 8;
    const orbitRadius = 50;
    const particleColors = [0x55FFAA, 0x55AAFF, 0xFFAA55, 0xAA55FF];
    
    for (let i = 0; i < numParticles; i++) {
        // Create particle
        const particle = this.add.circle(0, 0, 6, particleColors[i % particleColors.length], 0.8);
        particle.setData('orbitSpeed', 0.5 + (Math.random() * 0.5)); // Random speed
        particle.setData('orbitOffset', (Math.PI * 2 / numParticles) * i); // Distribute evenly
        particle.setData('pulsePhase', Math.random() * Math.PI * 2); // Random phase for pulsing
        
        // Add to container
        animContainer.add(particle);
        this.uiElements[`step_${stepNumber}_particle_${i}`] = particle;
    }
    
    // Create the wisdom orb (center circle)
    const wisdomOrb = this.add.circle(0, 0, 25, 0xFFFFFF, 0.7);
    this.uiElements[`step_${stepNumber}_wisdomOrb`] = wisdomOrb;
    animContainer.add(wisdomOrb);
    
    // Add subtle pulse to the orb
    this.tweens?.add({
        targets: wisdomOrb,
        scale: { from: 0.9, to: 1.1 },
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
    });
    
    // Create animation update callback
    this.uiElements[`step_${stepNumber}_animCallback`] = (time, delta) => {
        if (!animContainer?.active) return;
        
        // Update each particle's position around the orbit
        for (let i = 0; i < numParticles; i++) {
            const particle = this.uiElements[`step_${stepNumber}_particle_${i}`];
            if (!particle?.active) continue;
            
            const speed = particle.getData('orbitSpeed');
            const offset = particle.getData('orbitOffset');
            const angle = (time / 1000) * speed + offset;
            
            // Calculate orbit position
            particle.x = Math.cos(angle) * orbitRadius;
            particle.y = Math.sin(angle) * orbitRadius;
            
            // Add subtle pulsing to each particle
            const pulsePhase = particle.getData('pulsePhase');
            const pulseScale = 0.8 + (Math.sin(time / 400 + pulsePhase) * 0.3);
            particle.setScale(pulseScale);
            
            // Add slight alpha variation
            const alphaVar = 0.6 + (Math.sin(time / 500 + pulsePhase) * 0.4);
            particle.setAlpha(alphaVar);
        }
    };
    
    // Register the update callback
    this.events.on('update', this.uiElements[`step_${stepNumber}_animCallback`]);
    
    // Create subtle text that fades in and out beneath the animation
    const wisdomHintText = this.createThemedText(0, 0, "connecting to wisdom...", 'sizeSm', MenuUIUtils.COLORS.textMedium, 0.5);
    this.uiElements[`step_${stepNumber}_hintText`] = wisdomHintText;
    this.stepContainer.add(wisdomHintText);
    
    // Create text pulsing tween
    this.uiElements[`step_${stepNumber}_hintTween`] = this.tweens?.add({
        targets: wisdomHintText,
        alpha: { from: 0.3, to: 0.7 },
        duration: 1500,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1
    });
    break;
    // COMPLETE SOLUTION FOR PHILOSOPHY FLOW SCENE

// 1. FIRST FIX: STEP_DISPLAY_QUOTE case in createStepUIElements
// Replace the entire STEP_DISPLAY_QUOTE case in createStepUIElements with this:

case STEP_DISPLAY_QUOTE:
    if (this.quoteData?.quote) {
        // --- **FIX: Add quotes, italicize only the text** ---
        // Manually add quotes around the quote text stored in data
        const quoteDisplayString = `"${this.quoteData.quote}"`;

        // Create the text object, applying italic style with DeckOps design system colors
        this.uiElements[`step_${stepNumber}_quoteText`] = this.createThemedText(
            0, 0,
            quoteDisplayString, // Use the string with quotes added
            '22px',          // Font size
            0xffffff,        // Text color (pure white for primary text)
            0.5              // Origin
        ).setOrigin(0.5, 0)
         .setStyle({ fontStyle: 'italic' }); // Apply italic style here
        // --- **END FIX** ---

        // Use DeckOps design system colors: secondary text and muted text
        this.uiElements[`step_${stepNumber}_figureText`] = this.createThemedText(0, 0, `- ${this.quoteData.figure || 'Unknown'}`, '18px', 0x00d4ff, 0.5); // Primary brand color for figure
        this.uiElements[`step_${stepNumber}_reasoningText`] = this.createThemedText(0, 0, `Reasoning: ${this.quoteData.reasoning || '(Unavailable)'}`, '14px', 0xe2e8f0, 0.5).setOrigin(0.5, 0); // Light gray for reasoning
        this.uiElements[`step_${stepNumber}_doneButton`] = this.createStyledButton(0, 0, "DONE", () => this.returnToPreviousMenu(), 0x00d4ff); // Primary brand color

        // Create reasoning background panel
         const maxWidth = Math.min(this.cameras.main.width * 0.85, 600);
         const reasoningPanel = this.add.rectangle(0, 0, maxWidth, 100, 0x222222, 0.5).setOrigin(0.5, 0);
         this.uiElements[`step_${stepNumber}_reasoningPanel`] = reasoningPanel;

        // Add elements (panel first)
        this.stepContainer.add([
            this.uiElements[`step_${stepNumber}_reasoningPanel`],
            this.uiElements[`step_${stepNumber}_quoteText`],
            this.uiElements[`step_${stepNumber}_figureText`],
            this.uiElements[`step_${stepNumber}_reasoningText`],
            this.uiElements[`step_${stepNumber}_doneButton`]
        ]);
    } else { // Error State UI
        // ... (Error state code remains the same) ...
        this.uiElements[`step_${stepNumber}_errorTitle`] = this.createThemedText(0, 0, "Error Generating Quote", 'sizeLg', MenuUIUtils.COLORS.danger);
        this.uiElements[`step_${stepNumber}_errorMsg`] = this.createThemedText(0, 0, "Could not fetch wisdom.\nPlease try again later.", 'sizeMd', MenuUIUtils.COLORS.textMedium);
        this.uiElements[`step_${stepNumber}_backButton`] = this.createStyledButton(0, 0, "BACK TO MENU", () => this.returnToPreviousMenu(), MenuUIUtils.COLORS.textDark);
        this.stepContainer.add([ this.uiElements[`step_${stepNumber}_errorTitle`], this.uiElements[`step_${stepNumber}_errorMsg`], this.uiElements[`step_${stepNumber}_errorBackButton`] ]);
    }
    break; // End STEP_DISPLAY_QUOTE // End STEP_DISPLAY_QUOTE

            case STEP_RATE_LIMITED:
                 let nextTimeString = "tomorrow"; if (this.lastQuoteTimestamp?.toDate) { try { const nextTime = new Date(this.lastQuoteTimestamp.toDate().getTime() + (24*60*60*1000)); nextTimeString = nextTime.toLocaleString(); } catch(e){} }
                 this.uiElements[`step_${stepNumber}_info`] = this.createThemedText(0, 0, `Please return after:\n${nextTimeString}`, 'sizeMd', MenuUIUtils.COLORS.textMedium); this.uiElements[`step_${stepNumber}_backButton`] = this.createStyledButton(0, 0, "BACK TO MENU", () => sceneManager.switchToScene("AuthenticatedMenuScene"), MenuUIUtils.COLORS.textDark); this.stepContainer.add([ this.uiElements[`step_${stepNumber}_info`], this.uiElements[`step_${stepNumber}_backButton`] ]);
                 break;
        }
    }

    // --- Position UI elements ---
    // src/scenes/PhilosophyFlowScene.js

    // --- REPLACE positionStepUI METHOD ---
      // --- REPLACE positionStepUI METHOD ---
    /**
     * Positions the UI elements for the current step.
     */
    positionStepUI(stepNumber) {
        if (!this.stepContainer?.active) {
            console.warn(`Cannot position UI for step ${stepNumber}: Container inactive`);
            return;
        }

        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const centerX = width / 2;
        const contentStartY = height * 0.18; // Top position for the title
        const titleSpacing = 70; // Space below title
        let currentY = contentStartY + titleSpacing; // Base Y for content below title

        // Position Title first
        this.uiElements[`step_${stepNumber}_title`]?.setPosition(centerX, contentStartY);

        switch (stepNumber) {
            case STEP_EMOJI:
                const gridContainer = this.uiElements.emojiGridContainer;
                const confirmBtnEmoji = this.uiElements[`step_${stepNumber}_confirmBtn`];

                if (gridContainer?.active) {
                    // Calculate the Y position for the *center* of the grid
                    // Use the size we set on the container in createEmojiGrid
                    const gridHeight = gridContainer.height; // Height set via setSize
                    const gridCenterY = currentY + gridHeight / 2; // Position below title area

                    gridContainer.setPosition(centerX, gridCenterY); // Position the container

                    // Calculate button Y based on calculated grid bottom
                    // gridContainer.y is the center, so add half the height
                    const gridBottomY = gridCenterY + gridHeight / 2;
                    const confirmBtnY = gridBottomY + 55; // Position button below grid + spacing

                    if (confirmBtnEmoji?.active) {
                        confirmBtnEmoji.setPosition(centerX, confirmBtnY);
                        console.log(`Positioned Emoji Grid at Y: ${gridCenterY}, Confirm Button at Y: ${confirmBtnY}`);
                    } else {
                         console.warn("Emoji confirm button missing or inactive during positioning.");
                    }
                } else {
                     console.warn("Emoji grid container missing or inactive during positioning.");
                     if (confirmBtnEmoji?.active) { confirmBtnEmoji.setPosition(centerX, height * 0.8); } // Fallback
                }
                break;

            case STEP_COLOR:
                const wheelY = currentY + (WHEEL_SIZE / 2); // Center wheel vertically
                this.activeWheelSprite?.setPosition(centerX, wheelY);
                // Set initial selector position (can be refined)
                const initialSelectorAngle = -Math.PI / 2; // Top
                const initialSelectorRadius = WHEEL_SIZE / 2 - SELECTOR_SIZE;
                this.wheelSelectorSprite?.setPosition(
                    centerX + Math.cos(initialSelectorAngle) * initialSelectorRadius,
                    wheelY + Math.sin(initialSelectorAngle) * initialSelectorRadius
                );
                let confirmBtnYColor = wheelY + WHEEL_SIZE / 2 + 30; // Default Y below wheel
                 if (this.wheelColorDisplayRect?.active) {
                     const displayY = wheelY + WHEEL_SIZE / 2 + COLOR_DISPLAY_SIZE / 2 + 15;
                     this.wheelColorDisplayRect.setPosition(centerX, displayY);
                     this.updateColorSelection(this.selectedColorHue); // Update display
                     confirmBtnYColor = displayY + COLOR_DISPLAY_SIZE / 2 + 40; // Y below color box
                 }
                 this.uiElements[`step_${stepNumber}_confirmBtn`]?.setPosition(centerX, confirmBtnYColor);
                 break;

            case STEP_SLEEP:
                const options = []; let optionIndex = 0;
                while (this.uiElements[`step_${stepNumber}_button_${optionIndex}`]) { options.push(this.uiElements[`step_${stepNumber}_button_${optionIndex}`]); optionIndex++; }
                if (options.length > 0) {
                    const optionButtonHeight = 60; const optionSpacing = 15;
                    currentY += optionButtonHeight / 2; // Start Y for first button
                    options.forEach((el, index) => {
                         if(el?.active) el.setPosition(centerX, currentY + index * (optionButtonHeight + optionSpacing));
                    });
                }
                break;
  // Add this to the positionStepUI method
case STEP_GENERATING:
    const animContainer = this.uiElements[`step_${stepNumber}_animContainer`];
    const wisdomHintText = this.uiElements[`step_${stepNumber}_hintText`];
    
    // Position the title higher up
    this.uiElements[`step_${stepNumber}_title`]?.setPosition(centerX, height * 0.35);
    
    // Position animation container in the center
    if (animContainer?.active) {
        animContainer.setPosition(centerX, height * 0.52);
    }
    
    // Position hint text below animation
    if (wisdomHintText?.active) {
        wisdomHintText.setPosition(centerX, height * 0.65);
    }
    break;

                
          

            // Now let's improve the quote display positioning and text wrapping
// Now let's improve the quote display positioning and text wrapping
case STEP_DISPLAY_QUOTE:
    const quoteText = this.uiElements[`step_${stepNumber}_quoteText`];
    const figureText = this.uiElements[`step_${stepNumber}_figureText`];
    const reasoningText = this.uiElements[`step_${stepNumber}_reasoningText`];
    const reasoningPanel = this.uiElements[`step_${stepNumber}_reasoningPanel`];
    const doneButton = this.uiElements[`step_${stepNumber}_doneButton`];
    const errorTitle = this.uiElements[`step_${stepNumber}_errorTitle`];
    const errorMsg = this.uiElements[`step_${stepNumber}_errorMsg`];
    const errorBackButton = this.uiElements[`step_${stepNumber}_errorBackButton`];
    
    // Define max width for text
    const maxWidth = Math.min(width * 0.85, 600);
    let nextY = contentStartY + 20;
    
    if (quoteText?.active) {
        // Position quote text
        quoteText.setPosition(centerX, nextY);
        quoteText.setWordWrapWidth(maxWidth);
        nextY += quoteText.height + 25;
        
        // Position figure attribution
        if (figureText?.active) {
            figureText.setPosition(centerX, nextY);
            nextY += figureText.height + 40; // More space before reasoning
        }
        
        // Position reasoning text
        if (reasoningText?.active) {
            reasoningText.setPosition(centerX, nextY);
            reasoningText.setWordWrapWidth(maxWidth - 40);
            
            // Position and size the panel behind reasoning text
            if (reasoningPanel?.active) {
                const padding = 15;
                reasoningPanel.setPosition(centerX, nextY - padding);
                reasoningPanel.setSize(maxWidth, reasoningText.height + (padding * 2));
            }
            
            nextY += reasoningText.height + 40;
        }
        
        // Position done button
        if (doneButton?.active) {
            const buttonY = Math.min(nextY + 20, height - 70);
            doneButton.setPosition(centerX, buttonY);
        }
    } else if (errorTitle?.active) {
        // Position error elements
        errorTitle.setPosition(centerX, nextY);
        nextY += errorTitle.height + 20;
        
        if (errorMsg?.active) {
            errorMsg.setPosition(centerX, nextY);
            errorMsg.setWordWrapWidth(maxWidth);
            nextY += errorMsg.height + 30;
        }
        
        if (errorBackButton?.active) {
            errorBackButton.setPosition(centerX, height - 70);
        }
    }
    break;


            case STEP_RATE_LIMITED:
                 const rateTitle = this.uiElements[`step_${stepNumber}_title`];
                 const rateInfo = this.uiElements[`step_${stepNumber}_info`];
                 const rateBackBtn = this.uiElements[`step_${stepNumber}_backButton`];
                 let rateY = contentStartY + 50;
                 if (rateTitle?.active) { rateTitle.setPosition(centerX, rateY); rateY += rateTitle.height + 25; }
                 if (rateInfo?.active) { rateInfo.setPosition(centerX, rateY).setWordWrapWidth(width * 0.7); rateY += rateInfo.height + 40; }
                 if (rateBackBtn?.active) { rateBackBtn.setPosition(centerX, height - 90); }
                 break;

        } // End Switch
    }
    // --- END REPLACE positionStepUI ---


    // --- Grid and Wheel Methods ---
      // --- REPLACE createEmojiGrid METHOD ---
    /**
     * Creates the grid of interactive emoji buttons.
     * @param {Array} emojiData - Array of objects like { symbol, feeling }.
     */
    createEmojiGrid(emojiData) {
        if (!this.stepContainer || !this.add) return;

        const gridContainer = this.add.container(0, 0);
        this.uiElements.emojiGridContainer = gridContainer;
        this.stepContainer.add(gridContainer);

        const columns = 4;
        const buttonSize = 60;
        const buttonSpacing = 15;
        const gridWidth = columns * buttonSize + (columns - 1) * buttonSpacing;
        const gridRows = Math.ceil(emojiData.length / columns);
        const gridHeight = gridRows * buttonSize + (Math.max(0, gridRows - 1)) * buttonSpacing;
        const startX = -gridWidth / 2 + buttonSize / 2;
        const startY = -gridHeight / 2 + buttonSize / 2;

        emojiData.forEach((data, index) => {
            const row = Math.floor(index / columns);
            const col = index % columns;
            const x = startX + col * (buttonSize + buttonSpacing);
            const y = startY + row * (buttonSize + buttonSpacing);

            // --- Use the LOCAL createStyledButton which includes hover logic ---
            const button = this.createStyledButton(
                x, y,
                data.symbol,
                () => this.handleEmojiSelection(data, button), // Pass button ref
                0x1e293b, // DeckOps card background - rgba(255, 255, 255, 0.02) equivalent
                buttonSize, buttonSize,
                'sizeXl'
            );
            // --- End Use ---

            if (button) {
                button.setData('feeling', data.feeling);
                button.setData('isSelected', false); // *** ADD isSelected flag ***
                gridContainer.add(button);
                this.uiElements[`step_1_button_${index}`] = button;
            }
        });
        gridContainer.setSize(gridWidth, gridHeight);
    }
    // --- END REPLACE createEmojiGrid ---

      // --- REPLACE THIS METHOD ---
    /**
     * Handles the click event on an emoji button in the grid.
     * Updates the selected state and provides persistent visual feedback.
     * @param {object} selectedEmojiData - The data associated with the clicked emoji ({symbol, feeling}).
     * @param {Phaser.GameObjects.Container} clickedButton - The Phaser button container that was clicked.
     */
    handleEmojiSelection(selectedEmojiData, clickedButton) {
        if (this.isTransitioningSteps || !clickedButton?.active) return;

        const previouslySelectedButton = this.uiElements.selectedEmojiButton;

        // --- 1. Update State ---
        this.selectedFeeling = selectedEmojiData.feeling;
        this.selectedEmojiSymbol = selectedEmojiData.symbol;
        console.log(`Selected Feeling: ${this.selectedFeeling} (${this.selectedEmojiSymbol})`);

        // --- 2. Reset Previously Selected Button (if different) ---
        if (previouslySelectedButton && previouslySelectedButton !== clickedButton && previouslySelectedButton.active) {
            previouslySelectedButton.setData('isSelected', false); // Unset flag
            this.styleEmojiButton(previouslySelectedButton, false); // Apply inactive style
            console.log(`Reset previous selection: ${previouslySelectedButton.getData('feeling')}`);
        }

        // --- 3. Highlight Newly Selected Button ---
        clickedButton.setData('isSelected', true); // Set flag
        this.styleEmojiButton(clickedButton, true); // Apply active style
        console.log(`Highlighted current selection: ${selectedEmojiData.feeling}`);

        // --- 4. Store Reference ---
        this.uiElements.selectedEmojiButton = clickedButton;

        // --- 5. Feedback Tween (Optional) ---
        if (this.feedbackTween) this.feedbackTween.stop();
        this.feedbackTween = this.tweens?.add({ targets: clickedButton, scale: 0.95, duration: 80, yoyo: true });
    }

    createWheelUI(textureKey, type) {
         if (!this.textures.exists(textureKey)) { console.error(`Texture missing: ${textureKey}`); return; }
         this.activeWheelSprite = this.add.sprite(0, 0, textureKey).setInteractive();
         this.activeWheelSprite.setDisplaySize(WHEEL_SIZE, WHEEL_SIZE);
         this.stepContainer.add(this.activeWheelSprite);
         this.uiElements[`step_${this.currentStep}_wheel`] = this.activeWheelSprite;
         this.wheelSelectorSprite = this.add.circle(0, 0, SELECTOR_SIZE / 2, 0xffffff).setStrokeStyle(1, 0x000000).setDepth(1);
         this.stepContainer.add(this.wheelSelectorSprite);
         this.uiElements[`step_${this.currentStep}_selector`] = this.wheelSelectorSprite;
         const handler = (type === 'mood') ? this.handleColorWheelInput : null; // Only color wheel uses this handler now
         if(handler) {
            this.activeWheelSprite.on('pointerdown', handler, this);
            this.activeWheelSprite.on('pointermove', (pointer) => { if (pointer.isDown) { handler.call(this, pointer); } }, this);
         }
    }

    handleColorWheelInput(pointer) {
         if (!this.activeWheelSprite || !this.wheelSelectorSprite) return;
         const wheelCenterX = this.activeWheelSprite.x; const wheelCenterY = this.activeWheelSprite.y;
         const localX = pointer.x - wheelCenterX; const localY = pointer.y - wheelCenterY;
         let angleDeg = Phaser.Math.RadToDeg(Phaser.Math.Angle.Between(0, 0, localX, localY)); angleDeg = (angleDeg + 360) % 360;
         const segmentIndex = Math.floor(angleDeg / 30); const selectedData = COLOR_MOODS_MAP[segmentIndex];
         if (selectedData) {
             this.selectedMood = selectedData.mood; this.selectedHexColor = selectedData.color;
             const selectorRadius = WHEEL_SIZE / 2 - SELECTOR_SIZE; const selectorAngle = Phaser.Math.DegToRad(segmentIndex * 30 + 15);
             this.wheelSelectorSprite.x = wheelCenterX + Math.cos(selectorAngle) * selectorRadius; this.wheelSelectorSprite.y = wheelCenterY + Math.sin(selectorAngle) * selectorRadius;
             if (this.wheelColorDisplayRect) { this.wheelColorDisplayRect.setFillStyle(Phaser.Display.Color.HexStringToColor(this.selectedHexColor).color); }
         }
     }

    updateColorSelection(hue) { // Keep this for initial color display setup
         const colorObject = Phaser.Display.Color.HSLToColor(hue / 360, 1, 0.5); this.selectedHexColor = Phaser.Display.Color.RGBToString(colorObject.r, colorObject.g, colorObject.b, 255, '#');
         if (this.wheelColorDisplayRect) { this.wheelColorDisplayRect.setFillStyle(colorObject.color); }
     }

    // --- generateQuote (uses selectedFeeling, selectedMood) ---
    // src/scenes/PhilosophyFlowScene.js

    // --- generateQuote method ---
      // --- REPLACE generateQuote METHOD ---
     // --- REPLACE generateQuote METHOD ---
      // --- REPLACE generateQuote METHOD ---
    async generateQuote() {
        this.showStep(STEP_GENERATING);
        const minGeneratingTime = 2500;
        const startTime = Date.now();

        console.log(`--- Philosophy Flow - Preparing AI Prompt ---`);
        console.log(`Selected Feeling (Emoji): ${this.selectedFeeling} (${this.selectedEmojiSymbol || 'N/A'})`);
        console.log(`Selected Mood (Color): ${this.selectedMood} (${this.selectedHexColor})`);
        console.log(`Selected Sleep: ${this.selectedSleep}`);

        const prompt = `You are an API assistant that provides philosophical quotes.
Your task is to select a quote and philosopher relevant to the user's state, focusing on providing wisdom or perspective.
User's input: Feeling=${this.selectedFeeling || 'Unspecified'}, ColorMood=${this.selectedMood || 'Unspecified'} (${this.selectedHexColor}), Sleep=${this.selectedSleep}.

Based on the user's input, infer their likely overall state (e.g., stressed, content, seeking direction, tired).
Select an appropriate quote from a well-known philosopher (e.g., Marcus Aurelius, Nietzsche, Seneca, Epictetus, Camus, Sartre, de Beauvoir, Rumi, Lao Tzu, Viktor Frankl).
Aim for quotes that are: Calming if stressed/anxious. Uplifting if sad/tired. Thought-provoking if content/neutral. Motivating if seeking direction.

**CRITICAL:** Respond ONLY with the following exact format, with each part on a new line. Do NOT add any conversational text, questions, explanations, or markdown formatting outside of this structure:
QUOTE: [The quote text exactly as it should appear, without surrounding quotation marks]
FIGURE: [The philosopher's full name]
REASONING: [A short (1-2 sentence) explanation for why this quote/figure was chosen based ONLY on the user's input provided above.]`;

        console.log("Generated Prompt for AI:\n", prompt);

        try {
            console.log(`[${this.scene.key}-${SCENE_INSTANCE_ID}] Sending prompt to Claude API...`);
            const response = await this.claudeService.callClaudeApiWithRetry(prompt, 250);
            const extractedText = this.claudeService.extractResponse(response);
            console.log(`[${this.scene.key}-${SCENE_INSTANCE_ID}] Raw response from API:\n`, extractedText);

            // --- Parsing Logic ---
            const quoteMatchStrict = extractedText.match(/^QUOTE:\s*([\s\S]*?)\s*FIGURE:/im);
            const figureMatchStrict = extractedText.match(/^FIGURE:\s*([\s\S]*?)\s*REASONING:/im);
            const reasoningMatchStrict = extractedText.match(/^REASONING:\s*([\s\S]*?)$/im);

            let quoteText = quoteMatchStrict ? quoteMatchStrict[1].trim() : null;
            let figureText = figureMatchStrict ? figureMatchStrict[1].trim() : null;
            let reasoningText = reasoningMatchStrict ? reasoningMatchStrict[1].trim() : null;

            // --- **FIX 1: Strip quotes after extraction** ---
            if (quoteText) {
                quoteText = quoteText.replace(/^"|"$/g, '').trim(); // Remove leading/trailing quotes
            }
            // --- **END FIX 1** ---

            if (!quoteText || !figureText || !reasoningText) {
                 console.warn("Strict parsing failed or produced null values. Raw response:", extractedText);
                 // You could add flexible parsing here as a fallback if needed
                 throw new Error("Incomplete or invalid response format from AI");
            }

            // Store successfully parsed and cleaned data
            this.quoteData = { quote: quoteText, figure: figureText, reasoning: reasoningText };
            console.log("Parsed Quote Data:", this.quoteData);
            // --- End Parsing Logic ---


            // Update timestamp (Keep existing logic)
            const user = auth.currentUser;
            if (user && !this.isAdmin) {
                const userProfileRef = db.collection("userProfiles").doc(user.uid);
                await userProfileRef.set({ lastPhilosophyQuoteTimestamp: firebase.firestore.FieldValue.serverTimestamp() }, { merge: true });
                console.log(`Updated lastQuoteTimestamp for user ${user.uid}.`);
            }

            // Ensure minimum display time (Keep existing logic)
            const elapsedTime = Date.now() - startTime;
            if (elapsedTime < minGeneratingTime) await new Promise(resolve => setTimeout(resolve, minGeneratingTime - elapsedTime));

            // Display quote (Keep existing logic)
            if (this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) {
                this.showStep(STEP_DISPLAY_QUOTE);
            }

        } catch (error) { // Catch errors from API call OR parsing
            console.error(`[${this.scene.key}-${SCENE_INSTANCE_ID}] Error generating quote:`, error);
            // Set fallback quote data (ensure fallback quote doesn't have extra quotes)
            this.quoteData = {
                quote: "The obstacle is the way.", // No quotes here
                figure: "Marcus Aurelius",
                reasoning: "This quote was chosen as a fallback due to an error retrieving personalized wisdom."
            };
            // Ensure minimum display time even on error
            const elapsedTime = Date.now() - startTime;
            if (elapsedTime < minGeneratingTime) await new Promise(resolve => setTimeout(resolve, minGeneratingTime - elapsedTime));
            // Display the fallback quote step
            if (this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) {
                 this.showStep(STEP_DISPLAY_QUOTE);
            }
        }
    }

// COMPLETE METHOD for claudeAPIService.js
// Replace the entire callClaudeApiWithRetry method with this:

async callClaudeApiWithRetry(promptText, maxTokens = 250, attempts = 2) {
    let lastError = null;
    
    for (let attempt = 1; attempt <= attempts; attempt++) {
        try {
            console.log(`API request attempt ${attempt}/${attempts} for prompt: ${promptText.substring(0, 40)}...`);
            
            // Set a shorter timeout for the request
            const timeoutDuration = 5000; // 5 seconds instead of 15
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
                console.log(`API request attempt ${attempt} timed out after ${timeoutDuration}ms.`);
                controller.abort();
            }, timeoutDuration);
            
            try {
                // Call the Claude API (replace with your actual API call)
                const response = await fetch('/api/game/claude-proxy', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        prompt: promptText,
                        max_tokens_to_sample: maxTokens
                    }),
                    signal: controller.signal
                });
                
                // Clear the timeout since we got a response
                clearTimeout(timeoutId);
                
                // Check if the response is OK
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`HTTP Error ${response.status}: ${errorText}`);
                }
                
                // Parse the response
                const data = await response.json();
                
                console.log(`Claude API response received successfully via proxy`);
                return data;
            } catch (error) {
                // Clear the timeout if there was an error
                clearTimeout(timeoutId);
                
                // Rethrow the error to be caught by the outer try/catch
                throw error;
            }
            
        } catch (error) {
            lastError = error;
            console.error(`Attempt ${attempt}/${attempts} failed:`, error);
            
            // If this isn't the last attempt, wait before retrying
            if (attempt < attempts) {
                const delay = Math.pow(2, attempt) * 750; // Exponential backoff
                console.log(`Retrying in ${delay}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }
    
    // If we get here, all attempts failed
    console.error("All Claude API attempts failed after multiple retries.");
    
    // Return a formatted fallback response instead of throwing an error
    return {
        success: true,
        text: `QUOTE: The greatest wisdom is in simplicity. Love, respect, tolerance, sharing, gratitude, forgiveness. It's not complex or elaborate. The real knowledge is free. It's encoded in your DNA. All you need is within you. Great teachers have said that from the beginning. Find your heart, and you will find your way.
FIGURE: Carlos Barrios
REASONING: When technology fails us, we're reminded of the value of simplicity and inner wisdom. This quote was chosen as a reminder that profound insights don't always require external systems.`
    };
}
    // createStyledButton remains the same
    createStyledButton(x, y, text, callback, color, width = 180, height = 40, fontSizeKey = 'sizeMd') {
        // Use the MenuUIUtils version to get the base button container
        const buttonContainer = MenuUIUtils.createStyledButton(this, x, y, text, callback, {
            bgColor: color, width: width, height: height,
            fontSize: MenuUIUtils.FONTS[fontSizeKey] || MenuUIUtils.FONTS.sizeMd
        });

        if (!buttonContainer) return null; // Handle creation failure

        const bg = buttonContainer.getData('rectGraphic');
        const originalColor = buttonContainer.getData('originalColor');
        const cornerRadius = MenuUIUtils.SPACING.xs;

        // --- Override hover/out to respect isSelected ---
        buttonContainer.off('pointerover'); // Remove default hover from MenuUIUtils
        buttonContainer.off('pointerout');  // Remove default out from MenuUIUtils

        buttonContainer.on('pointerover', () => {
            if (!buttonContainer.active || !bg?.active || !buttonContainer.input?.enabled) return;
            // *** Only apply hover effect if NOT selected ***
            if (!buttonContainer.getData('isSelected')) {
                const hoverColor = Phaser.Display.Color.ValueToColor(originalColor).lighten(10).color;
                bg.clear().fillStyle(hoverColor, 1).fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
                this.tweens?.add({ targets: buttonContainer, scale: 1.03, duration: 100, ease: 'Sine.easeOut' });
            }
        });

        buttonContainer.on('pointerout', () => {
            if (!buttonContainer.active || !bg?.active) return;
            // *** Apply style based on isSelected ***
            this.styleEmojiButton(buttonContainer, buttonContainer.getData('isSelected'));
            this.tweens?.add({ targets: buttonContainer, scale: 1, duration: 100, ease: 'Sine.easeIn' });
        });
        // --- End Override ---

        // Keep existing pointerdown/up logic from MenuUIUtils (or recreate if needed)
        // Note: MenuUIUtils already handles pointerdown/up color changes and callback triggering.
        // If you need different click feedback specifically here, add it.

        return buttonContainer;
    }
    styleEmojiButton(buttonContainer, isActive) {
        if (!buttonContainer || !buttonContainer.active) return;

        const bgGraphics = buttonContainer.getData('rectGraphic');
        const width = buttonContainer.width;
        const height = buttonContainer.height;
        const cornerRadius = MenuUIUtils.SPACING.xs;
        const activeColor = 0x00d4ff; // DeckOps primary brand color for active
        const inactiveColor = 0x1e293b; // DeckOps secondary background for inactive

        if (bgGraphics?.clear) {
            bgGraphics.clear()
                      .fillStyle(isActive ? activeColor : inactiveColor, isActive ? 0.9 : 1) // Use correct color/alpha
                      .fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
        } else {
            console.warn("Cannot style emoji button, graphics object missing.");
        }
    }
    handleResize(gameSize) { if (!this.scene?.systems?.scenePlugin?.isActive(this.scene.key) || !this.cameras?.main?.width) { return; } if (this.uiElements.backButton?.active) { this.uiElements.backButton.setPosition(this.padding + 60, this.padding + 20); } super.handleResize(gameSize); if (this.uiElements.backButton?.active) { this.uiElements.backButton.setPosition(this.padding + 100, this.padding + 20); } if (this.currentStep && this.stepContainer?.active) { this.positionStepUI(this.currentStep); } }
    returnToPreviousMenu() { 
        const user = auth.currentUser; 
        const specialAccess = hasSpecialAccess(); 
        const isEffectivelyLoggedIn = (user && user.emailVerified) || specialAccess;
        
        // CRITICAL FIX: Use feature flag routing instead of hard-coded scene names
        const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
        const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
        const useDOMAuthMenu = localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true';
        
        let targetScene;
        if (useUniversalLanding) {
            targetScene = 'MainMenuSceneUniversal';
        } else if (isEffectivelyLoggedIn) {
            targetScene = useDOMAuthMenu ? "AuthenticatedMenuSceneDOM" : "AuthenticatedMenuScene";
        } else {
            // CRITICAL FIX: Always use DOM or Universal scenes
            const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
            targetScene = useUniversalLanding ? "MainMenuSceneUniversal" : "GuestMenuSceneDOM";
        }
        
        console.log(`PhilosophyFlowScene: Returning to menu - ${targetScene}`);
        this.startScene(targetScene); 
    }
    createBackButton() { this.uiElements.backButton = this.createStyledButton(this.padding + 60, this.padding + 20, "Back", () => this.returnToPreviousMenu(), MenuUIUtils.COLORS.secondary_dark, 100, 40 ); }
    shutdown() { console.log(`[${this.scene.key}-${SCENE_INSTANCE_ID}] Shutting down...`); this.clearStepUI(); if(this.uiElements.backButton) try { this.uiElements.backButton.destroy(); delete this.uiElements.backButton; } catch(e){} super.shutdown(); }
    
    // ENTERPRISE UPGRADE: Load template CSS for glass morphism consistency
    loadTemplateCSS() {
        if (!document.getElementById('philosophy-flow-template-css')) {
            // Load multiple template CSS files for complete styling
            const cssFiles = [
                '/enterprise-animation-standards.css',
                '/desktop-executive-dashboard-exact.css',
                '/ALL_TEMPLATES_EXACT.css'
            ];
            
            cssFiles.forEach((cssFile, index) => {
                const link = document.createElement('link');
                link.id = `philosophy-flow-template-css-${index}`;
                link.rel = 'stylesheet';
                link.href = `${cssFile}?v=${Date.now()}`;
                document.head.appendChild(link);
            });
            
            // Mark as loaded
            const marker = document.createElement('style');
            marker.id = 'philosophy-flow-template-css';
            marker.textContent = '/* Philosophy Flow Template CSS Loaded */';
            document.head.appendChild(marker);
            
            console.log('🎨 Philosophy Flow: Complete template CSS system loaded for glass morphism');
        }
    }

} // End PhilosophyFlowScene Class

export default PhilosophyFlowScene;